import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { LogoutManually, useAppDispatch } from "../../../redux";
import {
    Search, Filter, MapPin, Clock, AlertCircle, Phone, Mail, Camera, Video,
    ChevronLeft, ChevronRight, X, Check, Shield, Star, FileText, MessageSquare,
    DollarSign, UserCheck, Settings, Calendar, Bell, LogOut, User, Briefcase,
    Cog, CreditCard, HelpCircle, ChevronDown, Lock, BookOpen, FileCheck,
    AlertTriangle, Award, Building, Zap, Hammer, Plus, Clock3, Globe, Languages, ArrowUpCircle, Building2, MoveUp, ArrowUpDown, RefreshCw, Gift
} from "lucide-react";
import { getAllJobs, LogoutUser } from "../../../redux/Actions/AuthAction";
import { fetchCategoryFormSettings } from "../../../redux/Actions/CategoryFormSettingsAction";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import { fetchQuotesByJobIds } from "../../../redux/Slices/quoteSlice";
import { fetchQuoteFormSettingsList } from "../../../redux/Slices/quoteFormSettingsSlice";
import { fetchQuotePricings } from "../../../redux/Slices/AdminQuotePricingSlice";
import type { QuotePricingModel, QuotePricingRegion } from "../../shared/model/admin-quote-pricing.interfaces";
import { calculateQuotePrice } from "../../../utils/quotePricingUtils";
import Spinner from "../../shared/components/Spinner";
import { fetchLatestPromotionSetting } from "../../../redux/Slices/PromotionSettingSlice";
import { checkPromotionalEligibility } from "../../../redux/Actions/PromotionalQuoteActions";
import ReviewModal from "../../../components/ui/ReviewModal";
import ProfileCompletionDialog from "../../../components/ui/ProfileCompletionDialog";
import VerificationRequirementDialog from "../../../components/VerificationRequirementDialog";
import { useVerification } from "../../../hooks/useVerification";
import { checkUserBillingInfo } from "../../../utils/billingUtils";
import BillingAddressRequirementDialog from "../../../components/ui/BillingAddressRequirementDialog";
import { getDynamicCurrencySymbol } from "../../../lib/currencyUtils";
import { GlobalService } from "../../../modules";
import { getTranslatedCategoryName, getTranslatedSubcategory } from "../../../utils/categoryTranslation";

// import SettingsModal from './SettingsModal';
// import ProfileModal from './ProfileModal';
// import Tooltip from '../../components/ui/Tooltips';
// import SharedFooter3 from '../../Footer/SharedFooter3';
// import ChatPopup from './chat';
import Card from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import Button from "../../../components/ui/Button";
import RequestQuoteModal from "../../../components/ui/RequestQuoteModal";
import QuotePurchaseModal from "../../../components/ui/QuotePurchaseModal";
import Alert, { AlertDescription } from "../../../components/ui/alert";
import Tooltip from "../../../components/ui/Tooltips";
import SettingsModal from "../../../pages/Main/SettingsModal";
import ProfileModal from "../../../pages/Main/ProfileModal";
import SharedFooter3 from "../../../Footer/SharedFooter3";
import MobileMenu2 from "../../shared/components/MobileMenu";
import { JobStatusEnum, TypeEnum, RoleEnum } from "../../shared/model";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useUserCredits } from "../../../hooks/useUserCredits";
import { fetchUserCredits } from "../../../redux/Slices/creditSlice";
import { checkCanCommunicate } from "../../../redux/Actions/ProfessionalMonitoringActions";
import LanguageSelector from "../../../components/LanguageSelector";
import { FooterModalProvider } from "../../../contexts/FooterModalContext";

// Mock user data
const mockUserData = {
    hasSubscription: false,
    credits: 250,
    name: "John Smith",
    email: "john@example.com",
    profileImage: null,
    company: "Smith Plumbing Services",
    location: "Toronto, ON",
    isVerified: true,
    emergencyAvailable: true
};

const AllJobsComponent = ({ jobType = "local" }: { jobType?: "local" | "international" }) => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language.split("-")[0];
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isLoading = useSelector(selectLoaderValue);
    const { allJobs } = useSelector((state: any) => state.auth);
    const { settings: categoryFormSettings } = useSelector((state: any) => state.categoryFormSettings);
    const { settings: quoteFormSettings } = useSelector((state: any) => state.quoteFormSettings);

    // Get jobs data from allJobs structure
    const jobs = allJobs?.data || [];
    const totalPages = allJobs?.totalPages || 1;
    const totalCount = allJobs?.totalCount || 0;

    // State management
    const [activeTab, setActiveTab] = useState("quotes");
    const [professionalSearch, setProfessionalSearch] = useState("");
    const [provinceSearch, setProvinceSearch] = useState("");
    const [municipalitySearch, setMunicipalitySearch] = useState("");
    const [showQuoteDetails, setShowQuoteDetails] = useState(null);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedArea, setSelectedArea] = useState("all");
    const [sortBy, setSortBy] = useState("newest");
    const [showSettings, setShowSettings] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [requestType, setRequestType] = useState("quote");
    const [paymentMethod, setPaymentMethod] = useState("dollars");
    const [currentPage, setCurrentPage] = useState(() => {
        // Get current page from localStorage, default to 1
        const savedPage = localStorage.getItem("allJobsCurrentPage");
        return savedPage ? parseInt(savedPage, 10) : 1;
    });

    // Pagination constants
    const ITEMS_PER_PAGE = 6;
    
    // Calculate pagination values
    // Always calculate totalPages based on totalCount and ITEMS_PER_PAGE for accuracy
    // Don't trust backend totalPages as it may be calculated differently
    const calculatedTotalPages = totalCount > 0 ? Math.ceil(totalCount / ITEMS_PER_PAGE) : 1;
    const hasNextPage = currentPage < calculatedTotalPages;
    
    // Calculate range of items being displayed
    const startItem = totalCount > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
    const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalCount);
    const [showChat, setShowChat] = useState(false);
    const [showRequestQuoteModal, setShowRequestQuoteModal] = useState(false);
    const [selectedQuoteForRQ, setSelectedQuoteForRQ] = useState<any>(null);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState<any>(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedUserForReview, setSelectedUserForReview] = useState<any>(null);
    const [showQuotePurchaseModal, setShowQuotePurchaseModal] = useState(false);
    const [selectedQuoteForPurchase, setSelectedQuoteForPurchase] = useState<any>(null);
    const [purchasedQuotes, setPurchasedQuotes] = useState<any[]>([]);
    const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
    const [quoteResult, setQuoteResult] = useState<any[]>([]);
    const [quoteFormSettingsData, setQuoteFormSettingsData] = useState<any>(null);
    const [showProfileCompletionDialog, setShowProfileCompletionDialog] = useState(false);
    const [showVerificationDialog, setShowVerificationDialog] = useState(false);
    const [showBillingAddressDialog, setShowBillingAddressDialog] = useState(false);
    const [selectedQuoteForBilling, setSelectedQuoteForBilling] = useState<any>(null);
    const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [quotePricingsData, setQuotePricingsData] = useState<QuotePricingModel[]>([]);
    const [promotionSettings, setPromotionSettings] = useState<any>(null);
    const [promotionalEligibility, setPromotionalEligibility] = useState<Map<string, any>>(new Map());

    // Use verification hook
    const { isVerified, goToVerification, verificationData } = useVerification();
    const { isAuthenticated, user } = useSelector((state: any) => state.auth);
    const { total: userCredits, isLoading: creditsLoading, refetch: refetchCredits } = useUserCredits(user?.id);
    const { userBalance } = useSelector((state: any) => state.credits);
    
    // Track if verification data has been loaded at least once
    const [verificationDataLoaded, setVerificationDataLoaded] = useState(false);

    // State for credits array
    const [creditsArray, setCreditsArray] = useState([]);

    // Removed geocoding state; country and province now come from API

    // Function to fetch user credits and set in state
    const fetchUserCreditsData = async () => {
        if (!isAuthenticated || !user?.id) {
            setCreditsArray([]);
            return;
        }

        try {
            const result = await dispatch(fetchUserCredits({
                userId: user.id,
                query: { filters: JSON.stringify({ statusId: 1 }), page: 1, limit: 50 }
            }));

            if (fetchUserCredits.fulfilled.match(result)) {
                const records = result.payload?.data || [];

                const credits = records.filter((record: any) => record.subscriptionPlanArea ? (Number(record.subscriptionPlanArea.subscriptionPlan.type.id) === 2) : Number(record.subscriptionPlan.type.id) === 2);
                console.log("Credits response with statusId 1:", credits);
                // Set the response as-is in state
                setCreditsArray(credits || []);
            } else {
                setCreditsArray([]);
            }
        } catch (error: any) {
            console.error("Error fetching user credits:", error);
            setCreditsArray([]);
        }
    };

    // Function to get quote price based on settings and job type
    const getQuotePrice = (isQuote: boolean, isEmergency: boolean, job?: any) => {
        // Try to find matching quote pricing first if job is provided
        if (job && quotePricingsData.length > 0) {
            const jobDataForPricing = {
                categoryId: job?.categoryId?.id,
                province: job?.province,
                municipality: job?.municipality,
                city: job?.city,
                serviceArea: job?.serviceArea,
                isEmergency: isEmergency,
                isQuote: isQuote
            };

            const priceResult = calculateQuotePrice(quotePricingsData, jobDataForPricing, 20);
            
            if (priceResult.source === "quote_pricing") {
                console.log("Using quote pricing from utility:", priceResult);
                return priceResult.price;
            }
        }

        // Fallback to categoryFormSettings if no match found
        if (!categoryFormSettings) return 20; // Default fallback

        let settings;
        if (Array.isArray(categoryFormSettings) && categoryFormSettings.length > 0) {
            settings = categoryFormSettings[0];
        } else if (typeof categoryFormSettings === "object" && !Array.isArray(categoryFormSettings)) {
            settings = categoryFormSettings;
        } else {
            return 20; // Default fallback
        }

        if (isQuote && isEmergency) {
            return settings.emergencyQuotePrice || 20;
        } else if (isQuote && !isEmergency) {
            return settings.standardQuotePrice || 20;
        } else if (!isQuote && isEmergency) {
            return settings.emergencyRequestPrice || 20;
        }

        return 20; // Default fallback
    };

    // Helper function to check if a job is already purchased
    const isJobPurchased = (jobId: string) => {
        if (!Array.isArray(purchasedQuotes)) {
            return false;
        }
        return purchasedQuotes.some(quote => quote.job?.id === jobId);
    };

    // Helper function to get purchased quote for a job
    const getPurchasedQuote = (jobId: string) => {
        if (!Array.isArray(purchasedQuotes)) {
            return null;
        }
        return purchasedQuotes.find(quote => quote.job?.id === jobId);
    };

    // Function to check if a job qualifies for platform promotion
    // Function to check if job has promotional eligibility (new system: per-job quota)
    const isJobPromoted = (job: any): boolean => {
        // Only apply to quote jobs
        if (!job.isQuote) return false;

        // Check if auto-promotion is enabled
        if (!promotionSettings?.autoPromotion) return false;

        // Check if we have eligibility data for this job
        const eligibility = promotionalEligibility.get(job.id);
        return eligibility?.isEligible || false;
    };


    // Helper function to check if quote limit is exceeded for a job
    const isQuoteLimitExceeded = (jobId: string) => {
        if (!Array.isArray(quoteResult) || !quoteFormSettingsData?.regularMaximumRequest) {
            return false;
        }

        // Count quotes for this specific job
        const quotesForJob = quoteResult.filter(quote => quote.job?.id === jobId);
        const quoteCount = quotesForJob.length;
        return quoteCount >= quoteFormSettingsData.regularMaximumRequest;
    };

    // Helper function to check if user needs to complete profile
    // Check if user needs to complete profile
    const checkProfileCompletion = () => {
        if (isAuthenticated && user && !user.phone) {
            setShowProfileCompletionDialog(true);
            return false; // Profile incomplete
        }
        return true; // Profile complete
    };

    // Check if user needs verification
    const checkVerification = () => {
        // Don't check verification if data hasn't loaded yet
        // This prevents false positives when verification data is still loading
        if (!verificationDataLoaded) {
            console.log("⏳ Verification data not loaded yet, skipping verification check");
            return true; // Allow action to proceed while loading
        }
        
        if (isAuthenticated && user && !isVerified()) {
            console.log("❌ User is not verified, showing verification dialog");
            setShowVerificationDialog(true);
            return false; // Verification incomplete
        }
        
        console.log("✅ User is verified or verification check passed");
        return true; // Verification complete
    };

    // Check both profile and verification
    const checkRequirements = () => {
        // Don't block actions if verification data is still loading
        if (!verificationDataLoaded) {
            console.log("⏳ Verification data still loading, allowing action to proceed");
            // Only check profile completion while verification loads
            if (!checkProfileCompletion()) {
                return false;
            }
            return true;
        }
        
        // Check verification first (higher priority)
        if (!checkVerification()) {
            return false;
        }

        // Then check profile completion
        if (!checkProfileCompletion()) {
            return false;
        }

        return true;
    };

    // Helper function to navigate to profile based on user role
    const navigateToProfile = () => {
        if (!user?.role?.id) return;

        const roleId = Number(user.role.id);
        if (roleId === RoleEnum.professional || roleId === RoleEnum.internationl_professional) {
            navigate("/myprofile");
        } else if (roleId === RoleEnum.client || roleId === RoleEnum.internationl_client) {
            navigate("/client/profile");
        } else if (roleId === RoleEnum.admin) {
            navigate("/admin/dashboard");
        }
        setShowProfileCompletionDialog(false);
    };

    // Handle billing saved callback
    const handleBillingSaved = () => {
        // Wait a bit for Redux state to update after saving billing info
        setTimeout(() => {
            // Check if user now has billing information
            const billingCheck = checkUserBillingInfo(user);
            
            console.log("📋 Billing check after save:", billingCheck);
            
            if (billingCheck.hasBillingInfo && selectedQuoteForBilling) {
                // Set selected quote for purchase and open purchase modal
                setSelectedQuoteForPurchase({
                    id: selectedQuoteForBilling.id,
                    amount: getQuotePrice(selectedQuoteForBilling.isQuote, selectedQuoteForBilling.isEmergency, selectedQuoteForBilling),
                    job: {
                        id: selectedQuoteForBilling.id,
                        title: selectedQuoteForBilling.title
                    },
                    professional: {
                        id: user?.id,
                        firstName: user?.firstName,
                        lastName: user?.lastName
                    },
                    isEmergency: selectedQuoteForBilling.isEmergency,
                    useEmergencyCredits: selectedQuoteForBilling.isEmergency
                });
                setShowQuotePurchaseModal(true);
                setSelectedQuoteForBilling(null);
            } else {
                console.log("⚠️ Billing info not found after save, user might need to try again");
            }
        }, 500); // Small delay to allow Redux state to update
    };

    // Helper function to check if quote job has exceeded its validity period
    const isQuoteValidityExceeded = (job: any) => {
        console.log(job, "job11", quoteFormSettingsData, quoteFormSettingsData?.regularValidity);
        // Only apply to quote jobs
        // if (!job.isQuote) {
        //     return false;
        // }

        // Check if we have validity settings
        if (!quoteFormSettingsData?.regularValidity) {
            return false;
        }

        // Calculate days difference between job creation and today
        const jobCreatedDate = new Date(job.createdAt);
        const today = new Date();
        const timeDifference = today.getTime() - jobCreatedDate.getTime();
        const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24));
        console.log(daysDifference, "daysDifference", quoteFormSettingsData.regularValidity);
        // Return true if days difference is greater than regularValidity
        return daysDifference > quoteFormSettingsData.regularValidity;
    };

    // Function to calculate emergency cost with time and day multipliers
    const calculateEmergencyCost = (emergencyTimeSlot: any, emergencyDays: any) => {


        // Get settings from category form settings
        let settings = null;
        if (categoryFormSettings) {
            if (Array.isArray(categoryFormSettings) && categoryFormSettings.length > 0) {
                settings = categoryFormSettings[0];
            } else if (typeof categoryFormSettings === "object" && !Array.isArray(categoryFormSettings)) {
                settings = categoryFormSettings;
            }
        }

        if (!settings) return 80; // Default fallback

        // Get time slot and day type IDs
        const timeSlotId = Number(emergencyTimeSlot?.id);
        const dayTypeId = Number(emergencyDays?.id);

        // Determine the price based on time slot and day type
        let price = 80; // Default fallback

        if (timeSlotId === 1) { // Day (6 AM - 6 PM)
            if (dayTypeId === 1) { // Weekday
                price = settings.DayWeekdayPrice || 80;
            } else if (dayTypeId === 2) { // Weekend
                price = settings.DayWeekendPrice || 80;
            } else if (dayTypeId === 3) { // Holiday
                price = settings.DayHolidayPrice || 80;
            }
        } else if (timeSlotId === 2) { // Evening (6 PM - 10 PM)
            if (dayTypeId === 1) { // Weekday
                price = settings.EveningWeekdayPrice || 80;
            } else if (dayTypeId === 2) { // Weekend
                price = settings.EveningWeekendPrice || 80;
            } else if (dayTypeId === 3) { // Holiday
                price = settings.EveningHolidayPrice || 80;
            }
        } else if (timeSlotId === 3) { // Night (10 PM - 6 AM)
            if (dayTypeId === 1) { // Weekday
                price = settings.NightWeekdayPrice || 80;
            } else if (dayTypeId === 2) { // Weekend
                price = settings.NightWeekendPrice || 80;
            } else if (dayTypeId === 3) { // Holiday
                price = settings.NightHolidayPrice || 80;
            }
        }

        return price;
    };

    // Function to fetch jobs
    const fetchJobs = async () => {
        try {
            // Reset quotes loading state at the start
            setIsLoadingQuotes(false);
            
            const params: any = {
                page: currentPage,
                limit: ITEMS_PER_PAGE // Show 6 jobs per page (2 rows of 3 cards)
            };

            // Add location filters if provided
            const filters: any = {};

            // Add status filter to exclude inactive jobs (status 5)
            filters.status = [JobStatusEnum.Active, JobStatusEnum.Approved]; // Include all statuses except InActive (5)
            if (jobType === "international") {
                filters.isLocal = false;
            }
            else {
                filters.isLocal = true;
            }
            // Add search to filters if 2+ characters
            if (professionalSearch && professionalSearch.length >= 2) {
                filters.keyword = professionalSearch;
            }

            // Add province filter if 2+ characters
            if (provinceSearch && provinceSearch.length >= 2) {
                filters.province = provinceSearch;
            }

            // Add municipality filter if 2+ characters
            if (municipalitySearch && municipalitySearch.length >= 2) {
                filters.municipality = municipalitySearch;
            }

            if (selectedCategory !== "all") {
                filters.categoryId = selectedCategory;
            }
            if (selectedArea !== "all") {
                filters.serviceArea = selectedArea;
            }

            if (Object.keys(filters).length > 0) {
                params.filters = filters;
            }

            // Add sorting according to backend format
            const sortOptions = [];

            switch (sortBy) {
                case "newest":
                    sortOptions.push({ orderBy: "createdAt", order: "DESC" });
                    break;
                case "oldest":
                    sortOptions.push({ orderBy: "createdAt", order: "ASC" });
                    break;
                case "budget_high":
                    sortOptions.push({ orderBy: "budget", order: "DESC" });
                    break;
                case "budget_low":
                    sortOptions.push({ orderBy: "budget", order: "ASC" });
                    break;
                case "deadline_urgent":
                    sortOptions.push({ orderBy: "deadlineType", order: "DESC" });
                    break;
                case "deadline_relaxed":
                    sortOptions.push({ orderBy: "deadlineType", order: "ASC" });
                    break;
                default:
                    sortOptions.push({ orderBy: "createdAt", order: "DESC" });
            }

            // Always include sort parameter
            params.sort = sortOptions;

            const resultAction = await dispatch(getAllJobs({
                page: params.page,
                limit: params.limit,
                filters: params.filters,
                sort: params.sort
            }));

            // Call getQuotesByJobIds API if user is authenticated and jobs were fetched
            if (isAuthenticated && user?.id && resultAction.payload?.data?.length > 0) {
                const jobIds = resultAction.payload.data.map((job: any) => job.id);
                setIsLoadingQuotes(true);
                try {
                    const quotesResult = await dispatch(fetchQuotesByJobIds(jobIds)).unwrap();
                    console.log(quotesResult, "quotesResult");

                    // Store all quotes data in quoteResult state
                    if (Array.isArray(quotesResult)) {
                        setQuoteResult(quotesResult);
                    } else if (quotesResult && Array.isArray(quotesResult.data)) {
                        setQuoteResult(quotesResult.data);
                    } else {
                        setQuoteResult([]);
                    }

                    // Store the purchased quotes data (filtered by current user)
                    if (quotesResult.length > 0) {
                        const quotesData = quotesResult.filter((quote: any) => quote.user?.id === user.id);
                        console.log(quotesData, "quotesData");
                        // Ensure we always set an array
                        if (Array.isArray(quotesData)) {
                            setPurchasedQuotes(quotesData);
                        } else if (quotesData && Array.isArray(quotesData.data)) {
                            setPurchasedQuotes(quotesData.data);
                        } else {
                            setPurchasedQuotes([]);
                        }
                    } else {
                        setQuoteResult([]);
                        setPurchasedQuotes([]);
                    }
                } catch (error) {
                    console.error("Failed to fetch quotes by job IDs:", error);
                    setQuoteResult([]);
                    setPurchasedQuotes([]);
                } finally {
                    setIsLoadingQuotes(false);
                }
            } else {
                // No jobs or not authenticated, clear quotes data
                setQuoteResult([]);
                setPurchasedQuotes([]);
                setIsLoadingQuotes(false);
            }
        } catch (error) {
        } finally {
            // Mark initial load as complete
            setIsInitialLoad(false);
        }
    };

    // Reset page when jobType changes
    useEffect(() => {
        setCurrentPage(1);
        localStorage.setItem("allJobsCurrentPage", "1");
        setIsInitialLoad(true);
        // Clear promotional eligibility when changing job type
        setPromotionalEligibility(new Map());
    }, [jobType]);

    // Fetch jobs on component mount and when pagination/filters change (excluding search terms)
    useEffect(() => {
        fetchJobs();
    }, [dispatch, currentPage, selectedCategory, selectedArea, sortBy, jobType]);

    // Fetch quote form settings on component mount
    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchQuoteFormSettingsList({}));
        }
    }, [dispatch, isAuthenticated]);

    // Update local state when quote form settings are fetched
    useEffect(() => {
        if (isAuthenticated && quoteFormSettings && quoteFormSettings.length > 0) {
            setQuoteFormSettingsData(quoteFormSettings[0]); // Get the first setting or adjust as needed
        }
    }, [quoteFormSettings]);

    // Fetch category form settings on component mount
    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchCategoryFormSettings());
        }
    }, [dispatch, isAuthenticated]);

    // Fetch quote pricings on component mount
    useEffect(() => {
        const loadQuotePricings = async () => {
            try {
                const result = await dispatch(fetchQuotePricings({ page: 1, limit: 1000 }) as any);
                if (fetchQuotePricings.fulfilled.match(result)) {
                    setQuotePricingsData(result.payload?.data || []);
                    console.log("Quote pricings loaded:", result.payload?.data);
                }
            } catch (error) {
                console.error("Error fetching quote pricings:", error);
            }
        };
        loadQuotePricings();
    }, [dispatch]);

    // Fetch promotion settings on component mount
    useEffect(() => {
        const loadPromotionSettings = async () => {
            try {
                const result = await dispatch(fetchLatestPromotionSetting() as any);
                if (fetchLatestPromotionSetting.fulfilled.match(result)) {
                    setPromotionSettings(result.payload);
                    console.log("Promotion settings loaded:", result.payload);
                }
            } catch (error) {
                console.error("Error fetching promotion settings:", error);
            }
        };
        loadPromotionSettings();
    }, [dispatch]);

    // Fetch user credits when component mounts or user changes
    useEffect(() => {
        fetchUserCreditsData();
    }, [isAuthenticated, user?.id]);

    // Track when verification data has loaded
    useEffect(() => {
        if (verificationData !== null && verificationData !== undefined) {
            setVerificationDataLoaded(true);
        }
    }, [verificationData]);

    // Save current page to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem("allJobsCurrentPage", currentPage.toString());
    }, [currentPage]);

    // Debounced search effect for professional search - trigger after 2 characters or when cleared
    useEffect(() => {
        // If search is cleared (empty), reset immediately
        if (professionalSearch.length === 0) {
            // Reset to page 1 when clearing - this will trigger the main fetchJobs useEffect
            if (currentPage !== 1) {
                setCurrentPage(1);
                localStorage.setItem("allJobsCurrentPage", "1");
            } else {
                // Already on page 1, manually trigger fetch
                fetchJobs();
            }
            return;
        }
        
        // If search has 2+ characters, debounce the search
        if (professionalSearch.length >= 2) {
            const timeoutId = setTimeout(() => {
                // Reset to page 1 when searching - this will trigger the main fetchJobs useEffect
                if (currentPage !== 1) {
                    setCurrentPage(1);
                    localStorage.setItem("allJobsCurrentPage", "1");
                } else {
                    // Already on page 1, manually trigger fetch
                    fetchJobs();
                }
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [professionalSearch]);

    // Debounced search effect for province search - trigger after 2 characters or when cleared
    useEffect(() => {
        // If search is cleared (empty), reset immediately
        if (provinceSearch.length === 0) {
            // Reset to page 1 when clearing - this will trigger the main fetchJobs useEffect
            if (currentPage !== 1) {
                setCurrentPage(1);
                localStorage.setItem("allJobsCurrentPage", "1");
            } else {
                // Already on page 1, manually trigger fetch
                fetchJobs();
            }
            return;
        }
        
        // If search has 2+ characters, debounce the search
        if (provinceSearch.length >= 2) {
            const timeoutId = setTimeout(() => {
                // Reset to page 1 when searching - this will trigger the main fetchJobs useEffect
                if (currentPage !== 1) {
                    setCurrentPage(1);
                    localStorage.setItem("allJobsCurrentPage", "1");
                } else {
                    // Already on page 1, manually trigger fetch
                    fetchJobs();
                }
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [provinceSearch]);

    // Debounced search effect for municipality search - trigger after 2 characters or when cleared
    useEffect(() => {
        // If search is cleared (empty), reset immediately
        if (municipalitySearch.length === 0) {
            // Reset to page 1 when clearing - this will trigger the main fetchJobs useEffect
            if (currentPage !== 1) {
                setCurrentPage(1);
                localStorage.setItem("allJobsCurrentPage", "1");
            } else {
                // Already on page 1, manually trigger fetch
                fetchJobs();
            }
            return;
        }
        
        // If search has 2+ characters, debounce the search
        if (municipalitySearch.length >= 2) {
            const timeoutId = setTimeout(() => {
                // Reset to page 1 when searching - this will trigger the main fetchJobs useEffect
                if (currentPage !== 1) {
                    setCurrentPage(1);
                    localStorage.setItem("allJobsCurrentPage", "1");
                } else {
                    // Already on page 1, manually trigger fetch
                    fetchJobs();
                }
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [municipalitySearch]);

    // Handle return flow from credits page
    useEffect(() => {
        console.log("📍 AllJobsComponent location.state:", location.state);
        if (location.state?.openQuotePurchaseModal && location.state?.quoteId) {
            console.log("🎯 Opening quote purchase modal with quoteId:", location.state.quoteId);

            // Create the quote object for the modal
            const quoteForPurchase = {
                id: location.state.quoteId,
                amount: location.state.amount,
                job: {
                    id: location.state.jobId,
                    title: location.state.jobTitle
                },
                professional: {
                    id: location.state.professionalId,
                    firstName: location.state.professionalName?.split(" ")[0] || "",
                    lastName: location.state.professionalName?.split(" ").slice(1).join(" ") || ""
                },
                isEmergency: location.state.isEmergency,
                useEmergencyCredits: location.state.useEmergencyCredits
            };

            setSelectedQuoteForPurchase(quoteForPurchase);
            setShowQuotePurchaseModal(true);

            // Clear the location state to prevent reopening on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const handleLogout = async () => {
        try {
            const response = await dispatch(LogoutUser({})).unwrap();
        } catch (error: any) {
            if (error === 401) {
                dispatch(LogoutManually());
            } else {
            }
        }
    };


    // Menu items with notifications included
    const menuItems = [
        { icon: Globe, label: t("allJobsComponent.aboutKokobeo"), href: "/about" },
        { icon: HelpCircle, label: t("allJobsComponent.howItWorks"), href: "/how-it-works" },
        { icon: MessageSquare, label: t("allJobsComponent.messages"), href: "menu/messages", badge: "3" },
        { icon: Bell, label: t("allJobsComponent.notifications"), href: "/notifications", badge: "2" },
        { icon: Mail, label: t("allJobsComponent.contactUsMenu"), href: "menu/contact" },
        { icon: FileText, label: t("allJobsComponent.helpCenter"), href: "/help" },
        { icon: User, label: t("sidebar.myProfile"), href: "/client/profile" },
        { icon: Shield, label: t("sidebar.verifyAccount"), href: "/verify" },
        { icon: Star, label: t("allJobsComponent.subscriptionPlans"), href: "/plans" },
        { icon: CreditCard, label: t("allJobsComponent.myCredits"), href: "/credits", badge: mockUserData.credits },
        { icon: Briefcase, label: t("allJobsComponent.myProjects"), href: "/client/myorders", badge: t("allJobsComponent.active") }
    ];

    // Helper function to get relative time (e.g., "1 hour ago", "2 days ago")
    const getRelativeTime = (date: Date) => {
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) return "Just now";
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minute${Math.floor(diffInSeconds / 60) === 1 ? "" : "s"} ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hour${Math.floor(diffInSeconds / 3600) === 1 ? "" : "s"} ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} day${Math.floor(diffInSeconds / 86400) === 1 ? "" : "s"} ago`;
        if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} month${Math.floor(diffInSeconds / 2592000) === 1 ? "" : "s"} ago`;
        return `${Math.floor(diffInSeconds / 31536000)} year${Math.floor(diffInSeconds / 31536000) === 1 ? "" : "s"} ago`;
    };

    // Transform backend jobs data to match component structure
    const transformJobToQuote = (job: any) => ({
        id: job.id,
        userId: job.userId,
        type: job.isEmergency ? "emergency" : "standard",
        category: getTranslatedCategoryName(job.categoryId, currentLang) || job.categoryId?.name,
        categoryId: job.categoryId,
        subCategory: job.subCategoryId?.name || null,
        subCategories: job.subCategories || [], // Add subcategories array
        title: job.title || "Service Request",
        description: job.description || "Service request description",
        location: job.city || "Unknown Location",
        municipality: job.municipality || "Unknown Region/District",
        timePosted: job.createdAt ? getRelativeTime(new Date(job.createdAt)) : "Unknown",
        customer: {
            name: job.contactName || "Anonymous",
            phone: job.contactPhone || "N/A",
            email: job.contactEmail || "N/A",
            address: job.address || "N/A"
        },
        // User location coordinates
        userLatitude: job.userId?.latitude || job.user?.latitude,
        userLongitude: job.userId?.longitude || job.user?.longitude,
        userCountry: job.userId?.country || job.user?.country,
        userProvince: job.userId?.province || job.user?.province,
        hasPhotos: job.files && job.files.some((file: any) => {
            // Check if file is image (not video)
            const isVideoByType = file.type && file.type.startsWith("video/");
            const isVideoByExtension = file.path && (
                file.path.toLowerCase().endsWith(".mp4") ||
                file.path.toLowerCase().endsWith(".avi") ||
                file.path.toLowerCase().endsWith(".mov") ||
                file.path.toLowerCase().endsWith(".wmv") ||
                file.path.toLowerCase().endsWith(".flv") ||
                file.path.toLowerCase().endsWith(".webm") ||
                file.path.toLowerCase().endsWith(".mkv") ||
                file.path.toLowerCase().endsWith(".m4v") ||
                file.path.toLowerCase().endsWith(".3gp")
            );
            const isImageByType = file.type && file.type.startsWith("image/");
            const isImageByExtension = file.path && (
                file.path.toLowerCase().endsWith(".jpg") ||
                file.path.toLowerCase().endsWith(".jpeg") ||
                file.path.toLowerCase().endsWith(".png") ||
                file.path.toLowerCase().endsWith(".gif") ||
                file.path.toLowerCase().endsWith(".bmp") ||
                file.path.toLowerCase().endsWith(".webp") ||
                file.path.toLowerCase().endsWith(".svg")
            );
            // Return true if it's an image (by type or extension) and not a video
            return (isImageByType || isImageByExtension) && !isVideoByType && !isVideoByExtension;
        }),
        hasVideos: job.files && job.files.some((file: any) => {
            // Check if file is video by type or extension
            const isVideoByType = file.type && file.type.startsWith("video/");
            const isVideoByExtension = file.path && (
                file.path.toLowerCase().endsWith(".mp4") ||
                file.path.toLowerCase().endsWith(".avi") ||
                file.path.toLowerCase().endsWith(".mov") ||
                file.path.toLowerCase().endsWith(".wmv") ||
                file.path.toLowerCase().endsWith(".flv") ||
                file.path.toLowerCase().endsWith(".webm") ||
                file.path.toLowerCase().endsWith(".mkv") ||
                file.path.toLowerCase().endsWith(".m4v") ||
                file.path.toLowerCase().endsWith(".3gp")
            );
            return isVideoByType || isVideoByExtension;
        }),
        files: job.files || [], // Store actual file data
        price: job.amount || 20,
        hiring: true, // All jobs are available for hiring
        status: "New",
        // Additional backend fields
        budget: job.budget,
        budgetType: job.budgetType, // Added budgetType
        serviceType: job.serviceType,
        propertyType: job.propertyType,
        deadlineType: job.deadlineType,
        emergencyWhatsappNumber: job.emergencyWhatsappNumber,
        serviceArea: job.serviceArea,
        // Address fields for service location
        floor: job.floor,
        street: job.street,
        address: job.address,
        city: job.city,
        province: job.province,
        postalcode: job.postalCode,
        isElevatorAvailable: job.isElevatorAvailable,
        isStairsRequired: job.isStairsRequired,
        // Check if it's an emergency based on deadlineType
        isEmergency: Number(job.deadlineType?.id) === 5 || job.isEmergency,
        // Check if this is a quote request
        isQuote: job.isQuote,
        // Emergency details - map from the correct backend fields
        emergencyTimeSlot: job.emergencyTimeSlot,
        emergencyDays: job.emergencyDays,
        // Equipment fields
        equipmentTypeVal: job.equipmentTypeVal,
        equipmentBrand: job.equipmentBrand,
        equipmentModel: job.equipmentModel,
        equipmentPower: job.equipmentPower,
        equipmentYear: job.equipmentYear,
        otherEquipmentType: job.otherEquipmentType,
        // Lawyer job fields
        languages: job.languages,
        requiredExperience: job.requiredExperience,
        durationTimeline: job.durationTimeline,
        // Contact information
        contactWhatsapp: job.contactWhatsapp,
        // User rating data
        userRating: {
            averageRating: job.userId?.averageRating || job.user?.averageRating || 0,
            ratingCount: job.userId?.reviewsCount || 0,
            reviewsList: job.userId?.reviewsList || []
        }
    });

    // Use real jobs data from backend, fallback to empty array if loading
    // Filter out quote jobs that have exceeded their validity period
    const filteredJobs = jobs && jobs.length > 0 ? jobs.filter(job => !isQuoteValidityExceeded(job)) : [];
    const quotes = filteredJobs.length > 0 ? filteredJobs.map(transformJobToQuote) : [];

    // Check promotional eligibility for visible jobs
    // Backend validates:
    // 1. autoPromotion = true
    // 2. User account created within last 7 days
    // 3. Job has remaining promotional quota
    // 4. User hasn't already claimed this job
    useEffect(() => {
        if (quotes.length > 0 && promotionSettings?.autoPromotion && isAuthenticated && user?.id) {
            const checkEligibility = async () => {
                const newEligibility = new Map();
                for (const job of quotes.filter(j => j.isQuote)) {
                    try {
                        // Backend checks if user is within 1 week old AND other conditions
                        const result = await checkPromotionalEligibility(job.id, user.id);
                        newEligibility.set(job.id, result);
                        console.log(`Promotional eligibility for job ${job.id}:`, result);
                    } catch (error) {
                        console.error(`Failed to check eligibility for job ${job.id}:`, error);
                    }
                }
                setPromotionalEligibility(newEligibility);
            };
            checkEligibility();
        } else if (!promotionSettings?.autoPromotion) {
            // Clear eligibility if auto-promotion is disabled
            setPromotionalEligibility(new Map());
        }
    }, [quotes.length, promotionSettings?.autoPromotion, isAuthenticated, user?.id]);


    // Removed reverse geocoding side-effects

    // Handle refresh
    const handleRefresh = async () => {
        setIsInitialLoad(true);
        setCurrentPage(1);
        localStorage.setItem("allJobsCurrentPage", "1");
        setProfessionalSearch("");
        setProvinceSearch("");
        setMunicipalitySearch("");
        setSelectedCategory("all");
        setSelectedArea("all");
        setSortBy("newest");
        // Clear promotional eligibility to recalculate
        setPromotionalEligibility(new Map());
        // No geocoded locations to clear
        
        // Explicitly fetch jobs to refresh data
        await fetchJobs();
    };

    // Handle opening media modal
    const handleOpenMediaModal = (quote: any) => {
        setSelectedMedia(quote);
        setShowMediaModal(true);
    };

    // Helper function to safely convert object fields to display strings
    const safeDisplayValue = (value: any, fallback = "Not specified") => {
        if (!value) return fallback;
        if (typeof value === "string") return value;
        if (typeof value === "object") {
            return value.name || value.amount || value.value || value.id || fallback;
        }
        return String(value);
    };

    // Helper function to get floor suffix (1st, 2nd, 3rd, etc.)
    const getFloorSuffix = (floor: number) => {
        if (floor >= 11 && floor <= 13) return "th";
        switch (floor % 10) {
            case 1: return "st";
            case 2: return "nd";
            case 3: return "rd";
            default: return "th";
        }
    };

    // StarRatingDisplay component for showing user ratings
    const StarRatingDisplay = ({ rating, count, size = "sm", onClick }: { rating: number; count: number; size?: "sm" | "md" | "lg"; onClick?: () => void }) => {
        const sizeClasses = {
            sm: "h-3 w-3",
            md: "h-4 w-4",
            lg: "h-5 w-5"
        };

        return (
            <div className="flex items-center gap-1">
                <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => {
                        const isHalfFilled = rating >= star - 0.5 && rating < star;
                        const isFilled = rating >= star;

                        return (
                            <div key={star} className="relative">
                                <Star
                                    className={`${sizeClasses[size]} ${isFilled
                                        ? "fill-yellow-400 text-yellow-400"
                                        : "text-gray-300"
                                        }`}
                                />
                                {isHalfFilled && (
                                    <div className="absolute left-0 top-0 w-1/2 h-full overflow-hidden">
                                        <Star
                                            className={`${sizeClasses[size]} fill-yellow-400 text-yellow-400`}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <span className={`text-xs text-gray-600 ${onClick ? "cursor-pointer hover:text-blue-600" : ""}`} onClick={onClick}>
                    {rating > 0 ? rating.toFixed(1) : "0.0"} ({count} reviews)
                </span>
            </div>
        );
    };

    // Function to mask private information
    const maskPrivateInfo = (info: any, type: string, jobId?: string) => {
        // Handle null, undefined, or empty strings
        if (!info || typeof info !== "string" || info.trim() === "") {
            return "N/A";
        }

        // Only show unmasked information if job is actually purchased (not just promotional)
        // Client info will be revealed after professional claims/purchases the quote
        if (jobId && isJobPurchased(jobId)) {
            return info;
        }

        switch (type) {
            case "name":
                return info.replace(/(?!^.).(?!$)/g, "*");
            case "email": {
                if (!info.includes("@")) {
                    return "N/A";
                }
                const [localPart, domain] = info.split("@");
                if (!localPart || !domain || !domain.includes(".")) {
                    return "N/A";
                }
                const domainParts = domain.split(".");
                return `${localPart[0]}***@${domainParts[0][0]}***.${domainParts[1] || "com"}`;
            }
            case "phone":
                return info.replace(/\d(?=\d{4})/g, "*");
            case "address":
                return info.replace(/\d+|[a-zA-Z]+(?= )/g, "***");
            default:
                return info;
        }
    };

    // Quote card component
    const renderQuoteCard = (quote: any) => (
        <Card
            key={quote.id}
            className={`border ${quote.isEmergency
                ? "border-red-200 hover:border-red-300 bg-red-50/30"
                : "border-blue-200 hover:border-blue-300"
                } transition-all duration-300 hover:shadow-lg`}
        >
            <div className="p-4 sm:p-6 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        {quote.isEmergency && (
                            <Badge
                                variant="destructive"
                                className="bg-red-50 text-red-700"
                            >
                                {t("allJobsComponent.emergency")}
                            </Badge>
                        )}
                        {!quote.isQuote ? (
                            <Badge variant="outline" className="border-green-200 text-green-700">
                                {t("allJobsComponent.availableForHiring")}
                            </Badge>
                        ) : isJobPromoted(quote) ? (
                            <Badge variant="outline" className="border-purple-200 bg-purple-50 text-purple-700">
                                {t("allJobsComponent.platformPromotion")}
                            </Badge>
                        ) : (
                            <Badge variant="outline" className="border-blue-200 bg-blue-100 text-blue-700">
                                {t("allJobsComponent.requestQuote")}
                            </Badge>
                        )}

                    </div>
                    <div className="flex space-x-2">
                        {quote.hasPhotos &&
                            <Tooltip content={t("allJobsComponent.photosAvailable")}>
                                <button
                                    onClick={() => handleOpenMediaModal(quote)}
                                    className="hover:scale-110 transition-transform cursor-pointer"
                                >
                                    <Camera className="h-5 w-5 text-gray-500 hover:text-blue-600" />
                                </button>
                            </Tooltip>
                        }
                        {quote.hasVideos &&
                            <Tooltip content={t("allJobsComponent.videosAvailable")}>
                                <button
                                    onClick={() => handleOpenMediaModal(quote)}
                                    className="hover:scale-110 transition-transform cursor-pointer"
                                >
                                    <Video className="h-5 w-5 text-gray-500 hover:text-blue-600" />
                                </button>
                            </Tooltip>
                        }
                    </div>
                </div>

                {/* Content */}
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {quote.title || quote.category}
                    </h3>

                    {/* Category and Sub-category */}
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            {t("allJobsComponent.category")} {quote.category}
                        </Badge>
                        {quote.subCategories && quote.subCategories.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {quote.subCategories.slice(0, 2).map((subCat: string, index: number) => (
                                    <Badge key={index} variant="outline" className="text-xs bg-gray-50 text-gray-700 border-gray-200">
                                        {getTranslatedSubcategory(quote.categoryId, subCat, currentLang) || subCat}
                                    </Badge>
                                ))}
                                {quote.subCategories.length > 2 && (
                                    <Badge variant="outline" className="text-xs bg-gray-50 text-gray-700 border-gray-200">
                                        +{quote.subCategories.length - 2} more
                                    </Badge>
                                )}
                            </div>
                        )}
                    </div>

                    <p className="text-gray-600 text-sm line-clamp-2">
                        {quote.description || t("allJobsComponent.noDescriptionProvided")}
                    </p>

                    {/* User Rating */}
                    {quote.userRating && (quote.userRating.averageRating > 0 || quote.userRating.ratingCount > 0) && (
                        <div className="mt-3 flex items-center justify-center">
                            <div className="flex items-center gap-2 bg-yellow-50 px-3 py-1.5 rounded-lg">
                                <StarRatingDisplay
                                    rating={quote.userRating.averageRating}
                                    count={quote.userRating.ratingCount}
                                    size="sm"
                                    onClick={() => {
                                        console.log(quote.userId, quote);
                                        setSelectedUserForReview({ ...quote.userId, reviewsList: quote.userId.reviews });
                                        setShowReviewModal(true);
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Budget Information */}
                {quote.budget && (
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center justify-center gap-2 mb-1">
                            <span className="text-lg font-bold text-blue-600">{getDynamicCurrencySymbol()}</span>
                            <span className="text-sm font-medium text-blue-700">
                                {Number(quote.budgetType?.id) === 1 ? t("allJobsComponent.fixedPrice") :
                                    Number(quote.budgetType?.id) === 2 ? t("allJobsComponent.hourlyRate") : t("allJobsComponent.budget")}
                            </span>
                        </div>
                        <p className="text-lg font-bold text-blue-900">{getDynamicCurrencySymbol()}{quote.budget}</p>
                    </div>
                )}


                {/* Divider */}
                <div className="border-t border-gray-200"></div>

                {/* Customer Details */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <span className="text-gray-500 text-sm">{t("allJobsComponent.name")}</span>
                            <p className="font-medium">{maskPrivateInfo(quote.customer?.name, "name", quote.id)}</p>
                        </div>
                        <Button
                            variant="link"
                            className="text-blue-600 text-sm p-0 h-auto"
                            onClick={() => setShowQuoteDetails(quote)}
                        >
                            {t("allJobsComponent.seeDetails")}
                        </Button>
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <span className="text-gray-500 text-sm">{t("allJobsComponent.email")}</span>
                            <p className="text-blue-600">
                                {maskPrivateInfo(quote.customer?.email, "email", quote.id)}
                            </p>
                        </div>
                        <Button
                            variant="link"
                            className="text-blue-600 text-sm p-0 h-auto"
                            onClick={() => setShowQuoteDetails(quote)}
                        >
                            {t("allJobsComponent.seeDetails")}
                        </Button>
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <span className="text-gray-500 text-sm">{t("allJobsComponent.phone")}</span>
                            <p className="font-medium">{maskPrivateInfo(quote.customer?.phone, "phone", quote.id)}</p>
                        </div>
                        <Button
                            variant="link"
                            className="text-blue-600 text-sm p-0 h-auto"
                            onClick={() => setShowQuoteDetails(quote)}
                        >
                            {t("allJobsComponent.seeDetails")}
                        </Button>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center text-gray-500">
                            <MapPin className="h-4 w-4 mr-2" />
                            {quote.categoryId?.type?.id && Number(quote.categoryId.type.id) === TypeEnum.Home_Service ? (
                                <span>{quote.location} - {quote.municipality}</span>
                            ) : (
                                <span>
                                    {quote.userCountry || quote.userProvince ? (
                                        `${quote.userProvince || ""}${quote.userProvince && quote.userCountry ? ", " : ""}${quote.userCountry?.name || ""}`
                                    ) : (
                                        quote.userId?.address || t("allJobsComponent.locationNotAvailable")
                                    )}
                                </span>
                            )}
                        </div>
                        <span className="text-gray-400">{quote.timePosted}</span>
                    </div>
                </div>

                {/* Actions */}
                <div className="pt-4 border-t">
                    {!quote.isQuote ? (
                        <>                        <Button
                            className="w-full bg-red-600 hover:bg-red-700 text-white"
                            onClick={async () => {
                                if (!isAuthenticated) {
                                    setShowChat(true);
                                    return;
                                }

                                // Check if user needs to complete profile and verification
                                if (Number(user?.role?.id) != RoleEnum.client && !checkRequirements()) {
                                    return;
                                }

                                // If logged in and professional: check suspension status
                                if ((Number(user?.role?.id) === 4 || Number(user?.role?.id) === 5) && !quote.isQuote) {
                                    // Check communication restrictions for professionals
                                    try {
                                        const commCheck: any = await dispatch(checkCanCommunicate({ 
                                            professionalId: user.id, 
                                            clientId: quote.userId?.id || quote.userId 
                                        })).unwrap();
                                        
                                        if (!commCheck.canCommunicate) {
                                            const suspensionEndDate = commCheck.suspensionEndDate 
                                                ? new Date(commCheck.suspensionEndDate).toLocaleDateString() 
                                                : "unknown date";
                                            
                                            if (commCheck.suspensionType === "new_clients_only") {
                                                GlobalService.showToast(
                                                    "error", 
                                                    "Communication Restricted", 
                                                    `You are suspended from chatting with NEW CLIENTS until ${suspensionEndDate}. You can still chat with your existing clients.`
                                                );
                                            } else {
                                                GlobalService.showToast(
                                                    "error", 
                                                    "Communication Restricted", 
                                                    `You are suspended from chatting until ${suspensionEndDate}.`
                                                );
                                            }
                                            return;
                                        }
                                    } catch (error) {
                                        // Silent error - allow communication if check fails
                                    }

                                    setSelectedQuoteForRQ(quote);
                                    setShowRequestQuoteModal(true);
                                    return;
                                }
                                // Otherwise, fallback to existing behavior
                                setShowQuoteDetails(quote);
                            }}
                        >
                            {t("allJobsComponent.chatNow")}
                        </Button>
                        </>
                    ) : isJobPromoted(quote) ? (
                        <Button
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                            onClick={async () => {
                                if (!isAuthenticated) {
                                    navigate("/auth/register");
                                    return;
                                }

                                // Check if user needs to complete profile and verification
                                if (Number(user?.role?.id) != RoleEnum.client && !checkRequirements()) {
                                    return;
                                }

                                // If logged in and professional: check suspension status
                                if ((Number(user?.role?.id) === 4 || Number(user?.role?.id) === 5) && !quote.isQuote) {
                                    // Check communication restrictions for professionals
                                    try {
                                        const commCheck: any = await dispatch(checkCanCommunicate({ 
                                            professionalId: user.id, 
                                            clientId: quote.userId?.id || quote.userId 
                                        })).unwrap();
                                        
                                        if (!commCheck.canCommunicate) {
                                            const suspensionEndDate = commCheck.suspensionEndDate 
                                                ? new Date(commCheck.suspensionEndDate).toLocaleDateString() 
                                                : "unknown date";
                                            
                                            if (commCheck.suspensionType === "new_clients_only") {
                                                GlobalService.showToast(
                                                    "error", 
                                                    "Communication Restricted", 
                                                    `You are suspended from purchasing quotes from NEW CLIENTS until ${suspensionEndDate}. You can still purchase quotes from your existing clients.`
                                                );
                                            } else {
                                                GlobalService.showToast(
                                                    "error", 
                                                    "Communication Restricted", 
                                                    `You are suspended from purchasing quotes until ${suspensionEndDate}.`
                                                );
                                            }
                                            return;
                                        }
                                    } catch (error) {
                                        // Silent error - allow communication if check fails
                                    }

                                    setSelectedQuoteForPurchase({
                                        id: quote.id,
                                        amount: 0, // Free promotional quote
                                        job: {
                                            id: quote.id,
                                            title: quote.title
                                        },
                                        professional: {
                                            id: user?.id,
                                            firstName: user?.firstName,
                                            lastName: user?.lastName
                                        },
                                        isEmergency: quote.isEmergency,
                                        useEmergencyCredits: false,
                                        isPromotional: true // Flag to indicate this is a promotional quote
                                    });
                                    setShowQuotePurchaseModal(true);
                                    return;
                                }
                            }}
                        >
                            {t("allJobsComponent.freeQuotePlatformPromotion")}
                        </Button>
                    ) : (
                        <Button
                            className={`w-full ${isJobPurchased(quote.id) || isQuoteLimitExceeded(quote.id)
                                ? "bg-gray-500 hover:bg-gray-600 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700"
                                } text-white`}
                            onClick={async () => {
                                if (isJobPurchased(quote.id)) {
                                    // Show message that quote is already purchased
                                    alert(t("allJobsComponent.alreadyPurchasedQuote"));
                                    return;
                                }

                                if (isQuoteLimitExceeded(quote.id)) {
                                    // Show message that quote limit is exceeded
                                    alert(t("allJobsComponent.limitExceeds"));
                                    return;
                                }

                                if (!isAuthenticated) {
                                    navigate("/auth/register");
                                    return;
                                }

                                // Check if user needs to complete profile and verification
                                if (!checkRequirements()) {
                                    return;
                                }

                                // If logged in and professional: check suspension status
                                if ((Number(user?.role?.id) === 4 || Number(user?.role?.id) === 5) && !quote.isQuote) {
                                    // Check communication restrictions for professionals
                                    try {
                                        const commCheck: any = await dispatch(checkCanCommunicate({ 
                                            professionalId: user.id, 
                                            clientId: quote.userId?.id || quote.userId 
                                        })).unwrap();
                                        
                                        if (!commCheck.canCommunicate) {
                                            const suspensionEndDate = commCheck.suspensionEndDate 
                                                ? new Date(commCheck.suspensionEndDate).toLocaleDateString() 
                                                : "unknown date";
                                            
                                            if (commCheck.suspensionType === "new_clients_only") {
                                                GlobalService.showToast(
                                                    "error", 
                                                    "Communication Restricted", 
                                                    `You are suspended from purchasing quotes from NEW CLIENTS until ${suspensionEndDate}. You can still purchase quotes from your existing clients.`
                                                );
                                            } else {
                                                GlobalService.showToast(
                                                    "error", 
                                                    "Communication Restricted", 
                                                    `You are suspended from purchasing quotes until ${suspensionEndDate}.`
                                                );
                                            }
                                            return;
                                        }
                                    } catch (error) {
                                        // Silent error - allow communication if check fails
                                    }
                                }

                                // Check if user has billing information
                                const billingCheck = checkUserBillingInfo(user);
                                
                                if (!billingCheck.hasBillingInfo) {
                                    // Store the quote for auto-opening purchase modal after billing setup
                                    setSelectedQuoteForBilling(quote);
                                    // Show billing address requirement dialog
                                    setShowBillingAddressDialog(true);
                                    return;
                                }

                                if (Number(user?.role?.id) === 4 || Number(user?.role?.id) === 5) {
                                    setSelectedQuoteForPurchase({
                                        id: quote.id,
                                        amount: getQuotePrice(quote.isQuote, quote.isEmergency, quote),
                                        job: {
                                            id: quote.id,
                                            title: quote.title
                                        },
                                        professional: {
                                            id: user?.id,
                                            firstName: user?.firstName,
                                            lastName: user?.lastName
                                        },
                                        isEmergency: quote.isEmergency,
                                        useEmergencyCredits: quote.isEmergency
                                    });
                                    setShowQuotePurchaseModal(true);
                                    // Professional users can't purchase quotes
                                    return;
                                }
                            }}
                            disabled={isJobPurchased(quote.id) || isQuoteLimitExceeded(quote.id)}
                        >
                            {isJobPurchased(quote.id)
                                ? t("allJobsComponent.alreadyPurchasedQuote")
                                : isQuoteLimitExceeded(quote.id)
                                    ? t("allJobsComponent.limitExceeds")
                                    : quote.isEmergency
                                        ? t("allJobsComponent.purchaseQuoteCredits", { credits: getQuotePrice(quote.isQuote, quote.isEmergency, quote) })
                                        : t("allJobsComponent.purchaseQuoteDollars", { symbol: getDynamicCurrencySymbol(), amount: getQuotePrice(quote.isQuote, quote.isEmergency, quote) })
                            }
                        </Button>
                    )}
                </div>
            </div>
        </Card>
    );

    // Media Modal Component
    const MediaModal = ({ isOpen, onClose, media }: { isOpen: boolean; onClose: () => void; media: any }) => {
        if (!isOpen || !media) return null;

        return (
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.95 }}
                        className="bg-white rounded-xl w-[calc(100%-24px)] sm:w-full sm:max-w-[1200px] h-[calc(100%-24px)] sm:h-full sm:max-h-[800px] overflow-y-auto overflow-x-hidden"
                    >
                        {/* Header */}
                        <div className="p-4 sm:p-6 border-b flex justify-between items-center">
                            <div className="space-y-2">
                                <h3 className="text-xl font-semibold">{t("allJobsComponent.jobMedia")}</h3>
                                <p className="text-gray-600">{media.title || media.category}</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-4 sm:p-6">
                            {/* Photos Section */}
                            {media.hasPhotos && (
                                <div className="mb-8">
                                    <h4 className="text-lg font-medium mb-4 flex items-center gap-2">
                                        <Camera className="h-5 w-5 text-blue-600" />
                                        {t("allJobsComponent.photos", { count: media.files?.filter((file: any) => {
                                            // Check if file is image (not video)
                                            const isVideoByType = file.type && file.type.startsWith("video/");
                                            const isVideoByExtension = file.path && (
                                                file.path.toLowerCase().endsWith(".mp4") ||
                                                file.path.toLowerCase().endsWith(".avi") ||
                                                file.path.toLowerCase().endsWith(".mov") ||
                                                file.path.toLowerCase().endsWith(".wmv") ||
                                                file.path.toLowerCase().endsWith(".flv") ||
                                                file.path.toLowerCase().endsWith(".webm") ||
                                                file.path.toLowerCase().endsWith(".mkv") ||
                                                file.path.toLowerCase().endsWith(".m4v") ||
                                                file.path.toLowerCase().endsWith(".3gp")
                                            );
                                            const isImageByType = file.type && file.type.startsWith("image/");
                                            const isImageByExtension = file.path && (
                                                file.path.toLowerCase().endsWith(".jpg") ||
                                                file.path.toLowerCase().endsWith(".jpeg") ||
                                                file.path.toLowerCase().endsWith(".png") ||
                                                file.path.toLowerCase().endsWith(".gif") ||
                                                file.path.toLowerCase().endsWith(".bmp") ||
                                                file.path.toLowerCase().endsWith(".webp") ||
                                                file.path.toLowerCase().endsWith(".svg")
                                            );
                                            // Return true if it's an image (by type or extension) and not a video
                                            return (isImageByType || isImageByExtension) && !isVideoByType && !isVideoByExtension;
                                        }).length || 0 })}
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {media.files && media.files.length > 0 ? (
                                            media.files
                                                .filter((file: any) => {
                                                    // Check if file is image (not video)
                                                    const isVideoByType = file.type && file.type.startsWith("video/");
                                                    const isVideoByExtension = file.path && (
                                                        file.path.toLowerCase().endsWith(".mp4") ||
                                                        file.path.toLowerCase().endsWith(".avi") ||
                                                        file.path.toLowerCase().endsWith(".mov") ||
                                                        file.path.toLowerCase().endsWith(".wmv") ||
                                                        file.path.toLowerCase().endsWith(".flv") ||
                                                        file.path.toLowerCase().endsWith(".webm") ||
                                                        file.path.toLowerCase().endsWith(".mkv") ||
                                                        file.path.toLowerCase().endsWith(".m4v") ||
                                                        file.path.toLowerCase().endsWith(".3gp")
                                                    );
                                                    const isImageByType = file.type && file.type.startsWith("image/");
                                                    const isImageByExtension = file.path && (
                                                        file.path.toLowerCase().endsWith(".jpg") ||
                                                        file.path.toLowerCase().endsWith(".jpeg") ||
                                                        file.path.toLowerCase().endsWith(".png") ||
                                                        file.path.toLowerCase().endsWith(".gif") ||
                                                        file.path.toLowerCase().endsWith(".bmp") ||
                                                        file.path.toLowerCase().endsWith(".webp") ||
                                                        file.path.toLowerCase().endsWith(".svg")
                                                    );
                                                    // Return true if it's an image (by type or extension) and not a video
                                                    return (isImageByType || isImageByExtension) && !isVideoByType && !isVideoByExtension;
                                                })
                                                .map((file: any, index: number) => (
                                                    <div key={index} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                                                        {file.path ? (
                                                            <img
                                                                src={file.path}
                                                                alt={`Photo ${index + 1}`}
                                                                className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                                                                onClick={() => window.open(file.path, "_blank")}
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <div className="text-center">
                                                                    <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                                                    <p className="text-sm text-gray-500">{t("allJobsComponent.photo", { number: index + 1 })}</p>
                                                                    <p className="text-xs text-gray-400">{t("allJobsComponent.noPreview")}</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                        ) : (
                                            // Fallback to mock photos if no files available
                                            [1, 2, 3].map((photo, index) => (
                                                <div key={index} className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                                                    <div className="text-center">
                                                        <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                                        <p className="text-sm text-gray-500">Photo {index + 1}</p>
                                                        <p className="text-xs text-gray-400">Click to view</p>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Videos Section */}
                            {media.hasVideos && (
                                <div className="mb-8">
                                    <h4 className="text-lg font-medium mb-4 flex items-center gap-2">
                                        <Video className="h-5 w-5 text-blue-600" />
                                        {t("allJobsComponent.videos", { count: media.files?.filter((file: any) => {
                                            // Check if file is video by type or extension
                                            const isVideoByType = file.type && file.type.startsWith("video/");
                                            const isVideoByExtension = file.path && (
                                                file.path.toLowerCase().endsWith(".mp4") ||
                                                file.path.toLowerCase().endsWith(".avi") ||
                                                file.path.toLowerCase().endsWith(".mov") ||
                                                file.path.toLowerCase().endsWith(".wmv") ||
                                                file.path.toLowerCase().endsWith(".flv") ||
                                                file.path.toLowerCase().endsWith(".webm") ||
                                                file.path.toLowerCase().endsWith(".mkv") ||
                                                file.path.toLowerCase().endsWith(".m4v") ||
                                                file.path.toLowerCase().endsWith(".3gp")
                                            );
                                            return isVideoByType || isVideoByExtension;
                                        }).length || 0 })}
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {media.files && media.files.length > 0 ? (
                                            media.files
                                                .filter((file: any) => {
                                                    // Check if file is video by type or extension
                                                    const isVideoByType = file.type && file.type.startsWith("video/");
                                                    const isVideoByExtension = file.path && (
                                                        file.path.toLowerCase().endsWith(".mp4") ||
                                                        file.path.toLowerCase().endsWith(".avi") ||
                                                        file.path.toLowerCase().endsWith(".mov") ||
                                                        file.path.toLowerCase().endsWith(".wmv") ||
                                                        file.path.toLowerCase().endsWith(".flv") ||
                                                        file.path.toLowerCase().endsWith(".webm") ||
                                                        file.path.toLowerCase().endsWith(".mkv") ||
                                                        file.path.toLowerCase().endsWith(".m4v") ||
                                                        file.path.toLowerCase().endsWith(".3gp")
                                                    );
                                                    return isVideoByType || isVideoByExtension;
                                                })
                                                .map((file: any, index: number) => (
                                                    <div key={index} className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                                                        {file.path ? (
                                                            <video
                                                                src={file.path}
                                                                controls
                                                                className="w-full h-full object-cover"
                                                                preload="metadata"
                                                            >
                                                                <source src={file.path} type={file.type} />
                                                                Your browser does not support the video tag.
                                                            </video>
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <div className="text-center">
                                                                    <Video className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                                                    <p className="text-sm text-gray-500">{t("allJobsComponent.video", { number: index + 1 })}</p>
                                                                    <p className="text-xs text-gray-400">{t("allJobsComponent.noPreview")}</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                        ) : (
                                            // Fallback to mock videos if no files available
                                            [1, 2].map((video, index) => (
                                                <div key={index} className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                                                    <div className="text-center">
                                                        <Video className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                                        <p className="text-sm text-gray-500">Video {index + 1}</p>
                                                        <p className="text-xs text-gray-400">Click to play</p>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* No Media Message */}
                            {!media.hasPhotos && !media.hasVideos && (
                                <div className="text-center py-12">
                                    <Camera className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                    <h4 className="text-lg font-medium text-gray-900 mb-2">{t("allJobsComponent.noMediaAvailable")}</h4>
                                    <p className="text-gray-600">{t("allJobsComponent.noMediaDesc")}</p>
                                </div>
                            )}

                            {/* Job Details Summary */}
                            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                                <h4 className="font-medium mb-3">{t("allJobsComponent.jobSummary")}</h4>
                                <div className="grid grid-cols-1 gap-3 sm:gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-500">{t("allJobsComponent.category")}</span>
                                        <p className="font-medium">{media.category}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">{t("allJobsComponent.subCategories")}</span>
                                        <p className="font-medium break-words">
                                            {media.subCategories && media.subCategories.length > 0
                                                ? media.subCategories.map((subCat: string) => 
                                                    getTranslatedSubcategory(media.categoryId, subCat, currentLang) || subCat
                                                ).join(", ")
                                                : t("allJobsComponent.notSpecified")
                                            }
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">{t("allJobsComponent.location")}</span>
                                        {media.categoryId?.type?.id && Number(media.categoryId.type.id) === TypeEnum.Home_Service ? (
                                            <p className="font-medium">{media.location} - {media.municipality}</p>
                                        ) : (
                                            <p className="font-medium">
                                                {media.userCountry || media.userProvince ? (
                                                    `${media.userProvince || ""}${media.userProvince && media.userCountry ? ", " : ""}${media.userCountry.name || ""}`
                                                ) : (
                                                    media.userId?.address || t("allJobsComponent.locationNotAvailable")
                                                )}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <span className="text-gray-500">{t("allJobsComponent.description")}</span>
                                        <p className="font-medium line-clamp-2 break-words">{media.description}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">{t("allJobsComponent.posted")}</span>
                                        <p className="font-medium">{media.timePosted}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">{t("allJobsComponent.budget")}</span>
                                        <p className="font-medium">
                                            {media.budget ? `${getDynamicCurrencySymbol()}${media.budget}` : t("allJobsComponent.notSpecified")}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">{t("allJobsComponent.budgetType")}</span>
                                        <p className="font-medium">
                                            {Number(media.budgetType?.id) === 1 ? t("allJobsComponent.fixedPrice") :
                                                Number(media.budgetType?.id) === 2 ? t("allJobsComponent.hourlyRate") : t("allJobsComponent.notSpecified")}
                                        </p>
                                    </div>
                                    {media.categoryId?.type?.id && Number(media.categoryId.type.id) != TypeEnum.Home_Service && <><div>
                                        <span className="text-gray-500">{t("allJobsComponent.serviceType")}</span>
                                        <p className="font-medium">
                                            {Number(media.serviceType?.id) === 1 ? t("allJobsComponent.assistance") :
                                                Number(media.serviceType?.id) === 2 ? t("allJobsComponent.repair") :
                                                    Number(media.serviceType?.id) === 3 ? t("allJobsComponent.replacement") : t("allJobsComponent.notSpecified")}
                                        </p>
                                    </div><div>
                                            <span className="text-gray-500">{t("allJobsComponent.propertyType")}</span>
                                            <p className="font-medium">
                                                {Number(media.propertyType?.id) === 1 ? t("allJobsComponent.residential") :
                                                    Number(media.propertyType?.id) === 2 ? t("allJobsComponent.commercial") :
                                                        Number(media.propertyType?.id) === 3 ? t("allJobsComponent.industrial") : t("allJobsComponent.notSpecified")}
                                            </p>
                                        </div><div>
                                            <span className="text-gray-500">{t("allJobsComponent.deadline")}</span>
                                            <p className="font-medium">
                                                {Number(media.deadlineType?.id) === 1 ? t("allJobsComponent.informationOnly") :
                                                    Number(media.deadlineType?.id) === 2 ? t("allJobsComponent.withinAWeek") :
                                                        Number(media.deadlineType?.id) === 3 ? t("allJobsComponent.withinAMonth") :
                                                            Number(media.deadlineType?.id) === 4 ? t("allJobsComponent.futureMonth") :
                                                                Number(media.deadlineType?.id) === 5 ? t("allJobsComponent.emergency") : t("allJobsComponent.notSpecified")}
                                            </p>
                                        </div></>
                                    }
                                </div>

                                {/* Equipment Details - Show only for Repair (serviceType ID 2) */}
                                {(Number(media.serviceType?.id) === 2 || Number(media.serviceType?.id) === 3) && (
                                    <div className="mt-4 pt-4 border-t">
                                        <h4 className="font-medium mb-3">{t("allJobsComponent.equipmentDetails")}</h4>
                                        <div className="grid grid-cols-1 gap-3 sm:gap-4 text-sm">
                                            {media.equipmentTypeVal && (
                                                <div>
                                                    <span className="text-gray-500">{t("allJobsComponent.equipmentType")}</span>
                                                    <p className="font-medium">{media.equipmentTypeVal}</p>
                                                </div>
                                            )}
                                            {media.equipmentBrand && (
                                                <div>
                                                    <span className="text-gray-500">{t("allJobsComponent.brand")}</span>
                                                    <p className="font-medium">{media.equipmentBrand}</p>
                                                </div>
                                            )}
                                            {media.equipmentModel && (
                                                <div>
                                                    <span className="text-gray-500">{t("allJobsComponent.model")}</span>
                                                    <p className="font-medium">{media.equipmentModel}</p>
                                                </div>
                                            )}
                                            {media.equipmentPower && (
                                                <div>
                                                    <span className="text-gray-500">{t("allJobsComponent.power")}</span>
                                                    <p className="font-medium">{media.equipmentPower}</p>
                                                </div>
                                            )}
                                            {media.equipmentYear && (
                                                <div>
                                                    <span className="text-gray-500">{t("allJobsComponent.year")}</span>
                                                    <p className="font-medium">{media.equipmentYear}</p>
                                                </div>
                                            )}
                                            {media.otherEquipmentType && (
                                                <div>
                                                    <span className="text-gray-500">{t("allJobsComponent.otherType")}</span>
                                                    <p className="font-medium">{media.otherEquipmentType}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Lawyer Job Details - Show when isQuote is false */}
                                {media.categoryId?.type?.id && Number(media.categoryId.type.id) != TypeEnum.Home_Service && (
                                    <div className="mt-4 pt-4 border-t">
                                        <h4 className="font-medium mb-3">{t("allJobsComponent.jobRequirements")}</h4>
                                        <div className="grid grid-cols-1 gap-3 sm:gap-4 text-sm">
                                            {media.languages && media.languages.length > 0 && (
                                                <div>
                                                    <span className="text-gray-500">{t("allJobsComponent.requiredLanguages")}</span>
                                                    <p className="font-medium">
                                                        {media.languages.map((lang: any, index: number) => (
                                                            <span key={lang.id || index}>
                                                                {lang.name || lang.initial || `Language ${index + 1}`}
                                                                {index < media.languages.length - 1 ? ", " : ""}
                                                            </span>
                                                        ))}
                                                    </p>
                                                </div>
                                            )}
                                            {media.requiredExperience && (
                                                <div>
                                                    <span className="text-gray-500">{t("allJobsComponent.requiredExperience")}</span>
                                                    <p className="font-medium">
                                                        {Number(media.requiredExperience?.id) === 1 ? t("allJobsComponent.entryLevel") :
                                                            Number(media.requiredExperience?.id) === 2 ? t("allJobsComponent.intermediate") :
                                                                Number(media.requiredExperience?.id) === 3 ? t("allJobsComponent.expert") :
                                                                    Number(media.requiredExperience?.id) === 4 ? t("allJobsComponent.specialist") :
                                                                        t("allJobsComponent.notSpecified")}
                                                    </p>
                                                </div>
                                            )}
                                            {media.durationTimeline && (
                                                <div>
                                                    <span className="text-gray-500">{t("allJobsComponent.projectDuration")}</span>
                                                    <p className="font-medium">
                                                        {Number(media.durationTimeline?.id) === 1 ? t("allJobsComponent.lessThan1Month") :
                                                            Number(media.durationTimeline?.id) === 2 ? t("allJobsComponent.oneToThreeMonths") :
                                                                Number(media.durationTimeline?.id) === 3 ? t("allJobsComponent.threeToSixMonths") :
                                                                    Number(media.durationTimeline?.id) === 4 ? t("allJobsComponent.moreThan6Months") :
                                                                        t("allJobsComponent.notSpecified")}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 sm:p-6 border-t">
                            <div className="flex justify-end gap-3">
                                <Button
                                    variant="outline"
                                    onClick={onClose}
                                >
                                    {t("allJobsComponent.close")}
                                </Button>
                                <Button
                                    onClick={() => {
                                        onClose();
                                        setShowQuoteDetails(media);
                                    }}
                                >
                                    {t("allJobsComponent.viewFullDetails")}
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        );
    };

    // Chat Modal Component
    const ChatModal = ({ isOpen, onClose }) => {
        if (!isOpen) return null;

        return (
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.95 }}
                        className="bg-white rounded-xl max-w-2xl w-full h-[600px] flex flex-col"
                    >
                        <div className="p-4 border-b flex justify-between items-center">
                            <h3 className="text-lg font-semibold">{t("allJobsComponent.chatWithClient")}</h3>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="flex-1 p-4 overflow-y-auto">
                            <div className="space-y-4">
                                <div className="bg-gray-100 rounded-lg p-3 max-w-[80%]">
                                    <p className="text-sm">Hello! I&apos;m interested in your services.</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 border-t">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder={t("allJobsComponent.typeYourMessage")}
                                    className="flex-1 border rounded-lg px-4 py-2"
                                />
                                <Button onClick={() => {
                                    if (!isAuthenticated) {
                                        navigate("/auth/register");
                                        return;
                                    }
                                }}>{t("allJobsComponent.send")}</Button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        );
    };

    const QuoteDetailsModal = ({
        quote,
        isOpen,
        onClose,
        mockUserData = { hasSubscription: false },
        showChat,
        setShowChat,
        isAuthenticated,
        user,
        setSelectedQuoteForRQ,
        setShowRequestQuoteModal
    }) => {
        const [requestType, setRequestType] = useState("quote");
        const [paymentMethod, setPaymentMethod] = useState("dollars");

        if (!quote) return null;

        const handleHiringAction = async () => {
            if (quote.hiring) {
                if (!isAuthenticated) {
                    setShowChat(true);
                    return;
                }

                // Check if user needs to complete profile and verification
                if (!checkRequirements()) {
                    return;
                }

                // If logged in and professional: check suspension status
                if ((Number(user?.role?.id) === 4 || Number(user?.role?.id) === 5) && !quote.isQuote) {
                    // Check communication restrictions for professionals
                    try {
                        const commCheck: any = await dispatch(checkCanCommunicate({ 
                            professionalId: user.id, 
                            clientId: quote.userId?.id || quote.userId 
                        })).unwrap();
                        
                        if (!commCheck.canCommunicate) {
                            const suspensionEndDate = commCheck.suspensionEndDate 
                                ? new Date(commCheck.suspensionEndDate).toLocaleDateString() 
                                : "unknown date";
                            
                            if (commCheck.suspensionType === "new_clients_only") {
                                GlobalService.showToast(
                                    "error", 
                                    "Communication Restricted", 
                                    `You are suspended from chatting with NEW CLIENTS until ${suspensionEndDate}. You can still chat with your existing clients.`
                                );
                            } else {
                                GlobalService.showToast(
                                    "error", 
                                    "Communication Restricted", 
                                    `You are suspended from chatting until ${suspensionEndDate}.`
                                );
                            }
                            return;
                        }
                    } catch (error) {
                        // Silent error - allow communication if check fails
                    }

                    setSelectedQuoteForRQ(quote);
                    setShowRequestQuoteModal(true);
                    return;
                }
                // Otherwise, fallback to existing behavior
                setShowChat(true);
            } else {
                onClose();
            }
        };



        return (
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.95 }}
                            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                        >
                            {/* Header */}
                            <div className="p-6 border-b" style={{ textAlign: "left" }}>
                                <div className="flex items-center justify-between">
                                    <div className="space-y-2">
                                        <h3 className="text-xl font-semibold">{quote.title || quote.category || t("allJobsComponent.serviceRequest")}</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {quote.isEmergency && (
                                                <Badge
                                                    variant="destructive"
                                                    className="bg-red-50 text-red-700"
                                                >
                                                    {t("allJobsComponent.emergency")}
                                                </Badge>
                                            )}
                                            {!quote.isQuote && <Badge variant="outline" className="border-green-200 text-green-700">
                                                {t("allJobsComponent.availableForHiring")}
                                            </Badge>}
                                            {quote.isQuote && isJobPromoted(quote) && (
                                                <Badge variant="outline" className="border-purple-200 bg-purple-50 text-purple-700">
                                                    {t("allJobsComponent.platformPromotion")}
                                                </Badge>
                                            )}
                                            <Badge variant="default" className="bg-purple-50 text-purple-700">
                                                {t("allJobsComponent.customerChoice")} {Number(quote.deadlineType?.id) === 1 ? t("allJobsComponent.informationOnly") :
                                                    Number(quote.deadlineType?.id) === 2 ? t("allJobsComponent.withinAWeek") :
                                                        Number(quote.deadlineType?.id) === 3 ? t("allJobsComponent.withinAMonth") :
                                                            Number(quote.deadlineType?.id) === 4 ? t("allJobsComponent.futureMonth") :
                                                                Number(quote.deadlineType?.id) === 5 ? t("allJobsComponent.emergency") : t("allJobsComponent.informationOnly")}
                                            </Badge>
                                        </div>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="p-2 hover:bg-gray-100 rounded-full"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6 space-y-6" style={{ textAlign: "left" }}>
                                {/* Platform Promotion Notice */}
                                {quote.isQuote && isJobPromoted(quote) && (
                                    <Alert className="bg-purple-50 border-purple-200">
                                        <Gift className="h-4 w-4 text-purple-600" />
                                        <AlertDescription className="text-purple-700">
                                            {t("allJobsComponent.platformPromotionNotice")}
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {/* Emergency Notice */}
                                {quote.isEmergency && (
                                    <Alert className="bg-red-50 border-red-200">
                                        <AlertCircle className="h-4 w-4 text-red-600" />
                                        <AlertDescription className="text-red-700">
                                            {t("allJobsComponent.emergencyServiceNotice", { symbol: getDynamicCurrencySymbol(), amount: calculateEmergencyCost(quote.emergencyTimeSlot, quote.emergencyDays) })}
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {/* Request Type Tabs - For all services */}
                                <div className="space-y-4">
                                    <h4 className="font-medium">{t("allJobsComponent.requestType")}</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Button
                                            variant={quote.isQuote ? "default" : "outline"}
                                            onClick={() => setRequestType("quote")}
                                            className="w-full p-3 text-center"
                                        >
                                            {t("allJobsComponent.requestQuote")}
                                        </Button>
                                        <Button
                                            variant={!quote.isQuote ? "default" : "outline"}
                                            onClick={() => setRequestType("hiring")}
                                            className="w-full p-3 text-center"
                                        >
                                            {t("allJobsComponent.directHiring")}
                                        </Button>
                                    </div>
                                </div>

                                {/* Direct Hiring Features */}
                                {requestType === "hiring" && (
                                    <div className="space-y-4">
                                        <h4 className="font-medium">{t("allJobsComponent.directHiringIncludes")}</h4>
                                        <div className="space-y-3">
                                            <div className="flex items-center text-sm text-gray-600 space-x-2">
                                                <MessageSquare className="h-4 w-4 text-blue-500 shrink-0" />
                                                <span>{t("allJobsComponent.immediateChatWithCustomer")}</span>
                                            </div>
                                            <div className="flex items-center text-sm text-gray-600 space-x-2">
                                                <Clock className="h-4 w-4 text-blue-500 shrink-0" />
                                                <span>{t("allJobsComponent.realTimeServiceCoordination")}</span>
                                            </div>
                                            <div className="flex items-center text-sm text-gray-600 space-x-2">
                                                <Calendar className="h-4 w-4 text-blue-500 shrink-0" />
                                                <span>{t("allJobsComponent.directScheduling")}</span>
                                            </div>
                                            <div className="flex items-center text-sm text-gray-600 space-x-2">
                                                <Zap className="h-4 w-4 text-blue-500 shrink-0" />
                                                <span>{t("allJobsComponent.instantProfessionalAssistance")}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Service Location */}
                                {quote.categoryId?.type?.id && Number(quote.categoryId.type.id) === TypeEnum.Home_Service && <div className="space-y-4">
                                    <h4 className="font-medium">{t("allJobsComponent.serviceLocation")}</h4>
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.floorLevel")}</span>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <MoveUp className="h-4 w-4 text-gray-500" />
                                                    <p className="font-medium">
                                                        {quote.floor && Number(quote.floor) > 0
                                                            ? t("allJobsComponent.floor", { floor: Number(quote.floor), suffix: getFloorSuffix(Number(quote.floor)) })
                                                            : t("allJobsComponent.groundFloor")
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex gap-4">
                                                <div className="flex items-center gap-2">
                                                    <Building2 className="h-5 w-5 text-gray-500" />
                                                    <span className="text-sm text-gray-700">
                                                        {quote.isElevatorAvailable ? t("allJobsComponent.elevatorAvailable") : t("allJobsComponent.notSpecified")}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <ArrowUpDown className="h-5 w-5 text-gray-500" />
                                                    <span className="text-sm text-gray-700">
                                                        {quote.isStairsRequired ? t("allJobsComponent.stairsRequired") : t("allJobsComponent.notSpecifiedCaps")}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.street")}</span>
                                                <p className="font-medium">
                                                    {quote.street ? maskPrivateInfo(quote.street, "address") : t("allJobsComponent.notSpecified")}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.buildingType")}</span>
                                                <p className="font-medium">
                                                    {Number(quote.propertyType?.id) === 1 ? t("allJobsComponent.residential") :
                                                        Number(quote.propertyType?.id) === 2 ? t("allJobsComponent.commercial") :
                                                            Number(quote.propertyType?.id) === 3 ? t("allJobsComponent.industrial") : t("allJobsComponent.notSpecified")}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.address")}</span>
                                                <p className="font-medium">
                                                    {quote.address ? maskPrivateInfo(quote.address, "address") : t("allJobsComponent.notSpecified")}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.regionDistrict2")}</span>
                                                <p className="font-medium">
                                                    {quote.municipality || t("allJobsComponent.notSpecified")}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.province")}</span>
                                                <p className="font-medium">
                                                    {quote.province || t("allJobsComponent.notSpecified")}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.postalCode")}</span>
                                                <p className="font-medium">
                                                    {quote.postalcode || t("allJobsComponent.notSpecified")}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>}

                                {/* Request Details */}
                                <div className="space-y-4">
                                    <h4 className="font-medium">{t("allJobsComponent.requestDetails")}</h4>
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                                        {/* Category Information */}
                                        <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.category")}</span>
                                                <p className="font-medium">{quote.category}</p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.subCategories")}</span>
                                                <p className="font-medium">
                                                    {quote.subCategories && quote.subCategories.length > 0
                                                        ? quote.subCategories.map((subCat: string) => 
                                                            getTranslatedSubcategory(quote.categoryId, subCat, currentLang) || subCat
                                                        ).join(", ")
                                                        : t("allJobsComponent.notSpecified")
                                                    }
                                                </p>
                                            </div>
                                        </div>

                                        <div>
                                            <h5 className="font-medium text-gray-900 mb-2">{t("allJobsComponent.description")}</h5>
                                            <p className="text-gray-700">{quote.description || t("allJobsComponent.noDescriptionProvided")}</p>
                                        </div>

                                        {quote.budget && (
                                            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                                <div>
                                                    <span className="text-gray-500 text-sm">{t("allJobsComponent.budget")}</span>
                                                    <p className="font-medium">{getDynamicCurrencySymbol()}{safeDisplayValue(quote.budget, t("allJobsComponent.unknown"))}</p>
                                                </div>
                                                <div>
                                                    <span className="text-gray-500 text-sm">{t("allJobsComponent.budgetType")}</span>
                                                    <p className="font-medium">
                                                        {Number(quote.budgetType?.id) === 1 ? t("allJobsComponent.fixedPrice") :
                                                            Number(quote.budgetType?.id) === 2 ? t("allJobsComponent.hourlyRate") : t("allJobsComponent.notSpecified")}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {quote.categoryId?.type?.id && Number(quote.categoryId.type.id) === TypeEnum.Home_Service && <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.serviceType")}</span>
                                                <p className="font-medium">
                                                    {Number(quote.serviceType?.id) === 1 ? t("allJobsComponent.assistance") :
                                                        Number(quote.serviceType?.id) === 2 ? t("allJobsComponent.repair") :
                                                            Number(quote.serviceType?.id) === 3 ? t("allJobsComponent.replacement") : t("allJobsComponent.notSpecified")}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.propertyType")}</span>
                                                <p className="font-medium">
                                                    {Number(quote.propertyType?.id) === 1 ? t("allJobsComponent.residential") :
                                                        Number(quote.propertyType?.id) === 2 ? t("allJobsComponent.commercial") :
                                                            Number(quote.propertyType?.id) === 3 ? t("allJobsComponent.industrial") : t("allJobsComponent.notSpecified")}
                                                </p>
                                            </div>
                                        </div>}

                                        {quote.deadlineType && (
                                            <div className="pt-4 border-t">
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.deadline")}</span>
                                                <p className="font-medium">
                                                    {Number(quote.deadlineType?.id) === 1 ? t("allJobsComponent.informationOnly") :
                                                        Number(quote.deadlineType?.id) === 2 ? t("allJobsComponent.withinAWeek") :
                                                            Number(quote.deadlineType?.id) === 3 ? t("allJobsComponent.withinAMonth") :
                                                                Number(quote.deadlineType?.id) === 4 ? t("allJobsComponent.futureMonth") :
                                                                    Number(quote.deadlineType?.id) === 5 ? t("allJobsComponent.emergency") : t("allJobsComponent.notSpecified")}
                                                </p>
                                            </div>
                                        )}

                                        {/* Equipment Details - Show only for Repair (serviceType ID 2) */}
                                        {(Number(quote.serviceType?.id) === 2 || Number(quote.serviceType?.id) === 3) && (
                                            <div className="pt-4 border-t">
                                                <h5 className="font-medium text-gray-900 mb-3">{t("allJobsComponent.equipmentDetails")}</h5>
                                                <div className="grid grid-cols-2 gap-4">
                                                    {quote.equipmentTypeVal && (
                                                        <div>
                                                            <span className="text-gray-500 text-sm">{t("allJobsComponent.equipmentType")}</span>
                                                            <p className="font-medium">{quote.equipmentTypeVal}</p>
                                                        </div>
                                                    )}
                                                    {quote.equipmentBrand && (
                                                        <div>
                                                            <span className="text-gray-500 text-sm">{t("allJobsComponent.brand")}</span>
                                                            <p className="font-medium">{quote.equipmentBrand}</p>
                                                        </div>
                                                    )}
                                                    {quote.equipmentModel && (
                                                        <div>
                                                            <span className="text-gray-500 text-sm">{t("allJobsComponent.model")}</span>
                                                            <p className="font-medium">{quote.equipmentModel}</p>
                                                        </div>
                                                    )}
                                                    {quote.equipmentPower && (
                                                        <div>
                                                            <span className="text-gray-500 text-sm">{t("allJobsComponent.power")}</span>
                                                            <p className="font-medium">{quote.equipmentPower}</p>
                                                        </div>
                                                    )}
                                                    {quote.equipmentYear && (
                                                        <div>
                                                            <span className="text-gray-500 text-sm">{t("allJobsComponent.year")}</span>
                                                            <p className="font-medium">{quote.equipmentYear}</p>
                                                        </div>
                                                    )}
                                                    {quote.otherEquipmentType && (
                                                        <div>
                                                            <span className="text-gray-500 text-sm">{t("allJobsComponent.otherType")}</span>
                                                            <p className="font-medium">{quote.otherEquipmentType}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Lawyer Job Details - Show when isQuote is false */}
                                        {quote.categoryId?.type?.id && Number(quote.categoryId.type.id) != TypeEnum.Home_Service && (
                                            <div className="pt-4 border-t">
                                                <h5 className="font-medium text-gray-900 mb-3">{t("allJobsComponent.jobRequirements")}</h5>
                                                <div className="grid grid-cols-2 gap-4">
                                                    {quote.languages && quote.languages.length > 0 && (
                                                        <div>
                                                            <span className="text-gray-500 text-sm">{t("allJobsComponent.requiredLanguages")}</span>
                                                            <p className="font-medium">
                                                                {quote.languages.map((lang: any, index: number) => (
                                                                    <span key={lang.id || index}>
                                                                        {lang.name || lang.initial || `Language ${index + 1}`}
                                                                        {index < quote.languages.length - 1 ? ", " : ""}
                                                                    </span>
                                                                ))}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {quote.requiredExperience && (
                                                        <div>
                                                            <span className="text-gray-500 text-sm">{t("allJobsComponent.requiredExperience")}</span>
                                                            <p className="font-medium">
                                                                {Number(quote.requiredExperience?.id) === 1 ? t("allJobsComponent.entryLevel") :
                                                                    Number(quote.requiredExperience?.id) === 2 ? t("allJobsComponent.intermediate") :
                                                                        Number(quote.requiredExperience?.id) === 3 ? t("allJobsComponent.expert") :
                                                                            Number(quote.requiredExperience?.id) === 4 ? t("allJobsComponent.specialist") :
                                                                                t("allJobsComponent.notSpecified")}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {quote.durationTimeline && (
                                                        <div>
                                                            <span className="text-gray-500 text-sm">{t("allJobsComponent.projectDuration")}</span>
                                                            <p className="font-medium">
                                                                {Number(quote.durationTimeline?.id) === 1 ? t("allJobsComponent.lessThan1Month") :
                                                                    Number(quote.durationTimeline?.id) === 2 ? t("allJobsComponent.oneToThreeMonths") :
                                                                        Number(quote.durationTimeline?.id) === 3 ? t("allJobsComponent.threeToSixMonths") :
                                                                            Number(quote.durationTimeline?.id) === 4 ? t("allJobsComponent.moreThan6Months") :
                                                                                t("allJobsComponent.notSpecified")}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Customer Details */}
                                <div className="space-y-4">
                                    <h4 className="font-medium">{t("allJobsComponent.customerDetails")}</h4>
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.name")}</span>
                                                <p className="font-medium">{maskPrivateInfo(quote.customer?.name, "name", quote.id)}</p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.phone")}</span>
                                                <p className="font-medium">{maskPrivateInfo(quote.customer?.phone, "phone", quote.id)}</p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.email")}</span>
                                                <p className="font-medium">
                                                    {maskPrivateInfo(quote.customer?.email, "email", quote.id)}
                                                </p>
                                            </div>
                                            {quote.contactWhatsapp && (
                                                <div>
                                                    <span className="text-gray-500 text-sm">{t("allJobsComponent.whatsapp")}</span>
                                                    <p className="font-medium">{maskPrivateInfo(quote.contactWhatsapp, "phone", quote.id)}</p>
                                                </div>
                                            )}
                                            {quote.categoryId?.type?.id && Number(quote.categoryId.type.id) === TypeEnum.Home_Service && <div>
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.address")}</span>
                                                <p className="font-medium">{maskPrivateInfo(quote.customer?.address, "address", quote.id)}</p>
                                            </div>}
                                        </div>

                                        {/* User Rating */}
                                        {quote.userRating && (quote.userRating.averageRating > 0 || quote.userRating.ratingCount > 0) && (
                                            <div className="mt-4 pt-4 border-t">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-gray-500 text-sm">{t("allJobsComponent.customerRating")}</span>
                                                    <StarRatingDisplay
                                                        rating={quote.userRating.averageRating}
                                                        count={quote.userRating.ratingCount}
                                                        size="md"
                                                        onClick={() => {
                                                            setSelectedUserForReview({
                                                                id: quote.userId,
                                                                name: quote.customer?.name || "Anonymous",
                                                                firstName: quote.customer?.name?.split(" ")[0] || "Anonymous",
                                                                lastName: quote.customer?.name?.split(" ").slice(1).join(" ") || "",
                                                                title: quote.category || "Professional",
                                                                photo: undefined,
                                                                averageRating: quote.userRating.averageRating,
                                                                reviewsCount: quote.userRating.ratingCount,
                                                                reviewsList: quote.userRating.reviewsList || []
                                                            });
                                                            setShowReviewModal(true);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {quote.emergencyWhatsappNumber && (
                                            <div className="mt-4 pt-4 border-t">
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.emergencyWhatsapp")}</span>
                                                <p className="font-medium">{maskPrivateInfo(quote.emergencyWhatsappNumber, "phone")}</p>
                                            </div>
                                        )}

                                        {quote.serviceArea && (
                                            <div className="mt-4 pt-4 border-t">
                                                <span className="text-gray-500 text-sm">{t("allJobsComponent.serviceArea")}</span>
                                                <p className="font-medium">
                                                    {safeDisplayValue(quote.serviceArea?.name) || safeDisplayValue(quote.serviceArea?.id, t("allJobsComponent.unknown"))}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Attachments */}
                                <div className="space-y-4">
                                    <h4 className="font-medium">{t("allJobsComponent.attachments")}</h4>
                                    <div className="flex gap-4">
                                        {quote.hasPhotos && (
                                            <button
                                                onClick={() => {
                                                    setShowQuoteDetails(null);
                                                    setSelectedMedia(quote);
                                                    setShowMediaModal(true);
                                                }}
                                                className="bg-gray-50 p-3 rounded-lg  gap-2 hover:bg-gray-100 transition-colors cursor-pointer"
                                            >
                                                <span className="flex items-center gap-2">
                                                    <Camera className="h-5 w-5 text-blue-500" />
                                                    <span>{t("allJobsComponent.photosAvailable")}</span>
                                                </span>
                                                <span className="text-xs">{t("allJobsComponent.clickToSee")}</span>
                                            </button>
                                        )}
                                        {quote.hasVideos && (
                                            <button
                                                onClick={() => {
                                                    setShowQuoteDetails(null);
                                                    setSelectedMedia(quote);
                                                    setShowMediaModal(true);
                                                }}
                                                className="bg-gray-50 p-3 rounded-lg gap-2 hover:bg-gray-100 transition-colors cursor-pointer"
                                            >
                                                <span className="flex items-center gap-2">
                                                    <Video className="h-5 w-5 text-blue-500" />
                                                    <span>{t("allJobsComponent.videosAvailable")}</span>
                                                </span>
                                                <span className="text-xs">{t("allJobsComponent.clickToSee")}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Payment Method */}
                                {quote.isQuote && (
                                    <div className="space-y-4">
                                        <h4 className="font-medium">{t("allJobsComponent.paymentMethod")}</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <button
                                                onClick={() => setPaymentMethod("dollars")}
                                                className={`flex items-center justify-center gap-2 p-4 rounded-lg border transition-all ${paymentMethod === "dollars"
                                                    ? "bg-blue-600 text-white border-blue-600"
                                                    : "border-gray-200 hover:border-blue-200"
                                                    }`}
                                            >
                                                <span className="text-xl">$</span>
                                                <span className="font-medium">{t("allJobsComponent.payWithDollars")}</span>
                                            </button>
                                            <button
                                                onClick={() => setPaymentMethod("credits")}
                                                className={`flex items-center justify-center gap-2 p-4 rounded-lg border transition-all ${paymentMethod === "credits"
                                                    ? "bg-blue-600 text-white border-blue-600"
                                                    : "border-gray-200 hover:border-blue-200"
                                                    }`}
                                            >
                                                <span className="text-xl">💎</span>
                                                <span className="font-medium">{t("allJobsComponent.payWithCredits")}</span>
                                                {mockUserData?.hasSubscription && (
                                                    <span className="ml-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                                                        {t("allJobsComponent.locked")}
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Action Footer */}
                                <div className="pt-6 mt-6 border-t">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            {requestType === "hiring" || quote.isQuote === false || isJobPromoted(quote) ? (
                                                <><p className="text-2xl font-bold">{isJobPromoted(quote) ? t("allJobsComponent.freeQuote") : t("allJobsComponent.freeChat")}</p>
                                                    {!quote.isEmergency && !isJobPromoted(quote) && <p className="text-sm text-green-500">{t("allJobsComponent.availableForHiring")}</p>}
                                                    {isJobPromoted(quote) && <p className="text-sm text-purple-500">{t("allJobsComponent.platformPromotion")}</p>}
                                                    {/*<p className="text-sm">Client requests hiring</p> */}
                                                </>
                                            ) : (
                                                <>
                                                    <p className="text-2xl font-bold">
                                                        {quote.isEmergency
                                                            ? t("allJobsComponent.purchaseQuoteCredits", { credits: getQuotePrice(quote.isQuote, quote.isEmergency, quote) })
                                                            : paymentMethod === "dollars"
                                                                ? `${getDynamicCurrencySymbol()}${getQuotePrice(quote.isQuote, quote.isEmergency, quote)}.00`
                                                                : t("allJobsComponent.purchaseQuoteCredits", { credits: getQuotePrice(quote.isQuote, quote.isEmergency, quote) })
                                                        }
                                                    </p>
                                                    <p className="text-sm text-gray-500">{t("allJobsComponent.onetimeQuoteFee")}</p>
                                                </>
                                            )}
                                        </div>

                                        <Button
                                            onClick={async () => {
                                                if (!quote.isQuote || isJobPromoted(quote)) {
                                                    if (!quote.isQuote) {
                                                        handleHiringAction();
                                                    } else {
                                                        // Promoted quote - open purchase modal for free
                                                        if (!isAuthenticated) {
                                                            navigate("/auth/register");
                                                            return;
                                                        }

                                                        // Check if user needs to complete profile and verification
                                                        if (Number(user?.role?.id) != RoleEnum.client && !checkRequirements()) {
                                                            return;
                                                        }

                                                        // If logged in and professional: check suspension status
                                                        if ((Number(user?.role?.id) === 4 || Number(user?.role?.id) === 5) && !quote.isQuote) {
                                                            // Check communication restrictions for professionals
                                                            try {
                                                                const commCheck: any = await dispatch(checkCanCommunicate({ 
                                                                    professionalId: user.id, 
                                                                    clientId: quote.userId?.id || quote.userId 
                                                                })).unwrap();
                                                                
                                                                if (!commCheck.canCommunicate) {
                                                                    const suspensionEndDate = commCheck.suspensionEndDate 
                                                                        ? new Date(commCheck.suspensionEndDate).toLocaleDateString() 
                                                                        : "unknown date";
                                                                    
                                                                    if (commCheck.suspensionType === "new_clients_only") {
                                                                        GlobalService.showToast(
                                                                            "error", 
                                                                            "Communication Restricted", 
                                                                            `You are suspended from purchasing quotes from NEW CLIENTS until ${suspensionEndDate}. You can still purchase quotes from your existing clients.`
                                                                        );
                                                                    } else {
                                                                        GlobalService.showToast(
                                                                            "error", 
                                                                            "Communication Restricted", 
                                                                            `You are suspended from purchasing quotes until ${suspensionEndDate}.`
                                                                        );
                                                                    }
                                                                    return;
                                                                }
                                                            } catch (error) {
                                                                // Silent error - allow communication if check fails
                                                            }

                                                            setSelectedQuoteForPurchase({
                                                                id: quote.id,
                                                                amount: 0, // Free promotional quote
                                                                job: {
                                                                    id: quote.id,
                                                                    title: quote.title
                                                                },
                                                                professional: {
                                                                    id: user?.id,
                                                                    firstName: user?.firstName,
                                                                    lastName: user?.lastName
                                                                },
                                                                isEmergency: quote.isEmergency,
                                                                useEmergencyCredits: false,
                                                                isPromotional: true // Flag to indicate this is a promotional quote
                                                            });
                                                            setShowQuotePurchaseModal(true);
                                                            return;
                                                        }
                                                    }
                                                }
                                                else {
                                                    if (isJobPurchased(quote.id)) {
                                                        // Show message that quote is already purchased
                                                        alert("Already purchased quote");
                                                        return;
                                                    }

                                                    if (isQuoteLimitExceeded(quote.id)) {
                                                        // Show message that quote limit is exceeded
                                                        alert("Limit exceeds");
                                                        return;
                                                    }

                                                    if (!isAuthenticated) {
                                                        navigate("/auth/register");
                                                        return;
                                                    }

                                                    // Check if user needs to complete profile and verification
                                                    if (!checkRequirements()) {
                                                        return;
                                                    }

                                                    // If logged in and professional: check suspension status
                                                    if ((Number(user?.role?.id) === 4 || Number(user?.role?.id) === 5) && !quote.isQuote) {
                                                        // Check communication restrictions for professionals
                                                        try {
                                                            const commCheck: any = await dispatch(checkCanCommunicate({ 
                                                                professionalId: user.id, 
                                                                clientId: quote.userId?.id || quote.userId 
                                                            })).unwrap();
                                                            
                                                            if (!commCheck.canCommunicate) {
                                                                const suspensionEndDate = commCheck.suspensionEndDate 
                                                                    ? new Date(commCheck.suspensionEndDate).toLocaleDateString() 
                                                                    : "unknown date";
                                                                
                                                                if (commCheck.suspensionType === "new_clients_only") {
                                                                    GlobalService.showToast(
                                                                        "error", 
                                                                        "Communication Restricted", 
                                                                        `You are suspended from purchasing quotes from NEW CLIENTS until ${suspensionEndDate}. You can still purchase quotes from your existing clients.`
                                                                    );
                                                                } else {
                                                                    GlobalService.showToast(
                                                                        "error", 
                                                                        "Communication Restricted", 
                                                                        `You are suspended from purchasing quotes until ${suspensionEndDate}.`
                                                                    );
                                                                }
                                                                return;
                                                            }
                                                        } catch (error) {
                                                            // Silent error - allow communication if check fails
                                                        }
                                                    }

                                                    // Check if user has billing information
                                                    const billingCheck = checkUserBillingInfo(user);
                                                    
                                                    if (!billingCheck.hasBillingInfo) {
                                                        // Store the quote for auto-opening purchase modal after billing setup
                                                        setSelectedQuoteForBilling(quote);
                                                        // Show billing address requirement dialog
                                                        setShowBillingAddressDialog(true);
                                                        return;
                                                    }

                                                    if (Number(user?.role?.id) === 4 || Number(user?.role?.id) === 5) {
                                                        setSelectedQuoteForPurchase({
                                                            id: quote.id,
                                                            amount: getQuotePrice(quote.isQuote, quote.isEmergency, quote),
                                                            job: {
                                                                id: quote.id,
                                                                title: quote.title
                                                            },
                                                            professional: {
                                                                id: user?.id,
                                                                firstName: user?.firstName,
                                                                lastName: user?.lastName
                                                            },
                                                            isEmergency: quote.isEmergency,
                                                            useEmergencyCredits: quote.isEmergency
                                                        });
                                                        setShowQuotePurchaseModal(true);
                                                        // Professional users can't purchase quotes
                                                        return;
                                                    }

                                                }
                                            }}
                                            size="lg"
                                            className={`px-6 py-2 text-white ${!quote.isQuote || isJobPromoted(quote)
                                                ? isJobPromoted(quote) 
                                                    ? "bg-purple-600 hover:bg-purple-700"
                                                    : "bg-red-600 hover:bg-red-700"
                                                : isJobPurchased(quote.id) || isQuoteLimitExceeded(quote.id)
                                                    ? "bg-gray-500 hover:bg-gray-600 cursor-not-allowed"
                                                    : "bg-blue-600 hover:bg-blue-700"
                                                }`}
                                            disabled={quote.isQuote && !isJobPromoted(quote) && (isJobPurchased(quote.id) || isQuoteLimitExceeded(quote.id))}
                                        >
                                            {!quote.isQuote 
                                                ? t("allJobsComponent.startChat")
                                                : isJobPromoted(quote)
                                                    ? t("allJobsComponent.contactClientFree")
                                                    : (isJobPurchased(quote.id)
                                                        ? t("allJobsComponent.alreadyPurchasedQuote")
                                                        : isQuoteLimitExceeded(quote.id)
                                                            ? t("allJobsComponent.limitExceeds")
                                                            : t("allJobsComponent.requestQuote"))
                                            }
                                            {/* {quote.isEmergency ? "Respond Now" :
                                                (requestType === "hiring" || quote.isQuote === false) ? "Start Chat" : "Purchase Quote"} */}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )
                }
            </AnimatePresence >
        );
    };

    // Removed reverseGeocode function; location comes from API

    return (
        <FooterModalProvider>
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Full Screen Loader with Backdrop */}
            {showFullScreenLoader && (
                <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-8 flex flex-col items-center justify-center shadow-xl max-w-md mx-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t("allJobsComponent.processingPurchase")}</h3>
                        <p className="text-gray-600 text-center">{t("allJobsComponent.processingPurchaseDesc")}</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <header className="bg-white border-b sticky top-0 z-50" >
                <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4">
                    {/* Logo and Navigation */}
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            {/* User Menu */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-xl"
                                >
                                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                                        <User className="h-5 w-5 text-white" />
                                    </div>
                                    <ChevronDown className="h-4 w-4 text-gray-500" />
                                </button>

                                <MobileMenu2
                                    isOpen={showUserMenu}
                                    onClose={() => setShowUserMenu(false)}
                                    isAuthenticated={isAuthenticated}
                                    onLogout={handleLogout}
                                    currentUser={user} />

                            </div>

                            <a href="/" className="flex items-center gap-2 mr-8">
                                <img
                                    src="https://assests.netlify.app/assets/images/logo.png"
                                    alt="Kokobeo"
                                    className="h-8 w-auto"
                                />
                                <span className="text-xl font-bold text-blue-600">Kokobeo</span>
                            </a>
                        </div>

                        {/* Right side controls */}
                        <div className="flex items-center gap-2 sm:gap-4">
                            {/* Credits Display */}
                            <div className="hidden sm:flex items-center bg-blue-50 rounded-xl px-4 py-1.5">
                                <DollarSign className="h-4 w-4 mr-1 text-blue-600" />
                                <span className="text-sm font-medium text-blue-700">
                                    {t("allJobsComponent.creditsBadge", { credits: userCredits })}
                                </span>
                            </div>

                            {/* Language Selector */}
                            <LanguageSelector variant="modal" />

                            {/* Client Area Button */}
                            {/* <Button
                                variant="outline"
                                style={{ fontSize: "12px" }}
                            >
                                <Link to={jobType === "international" ? "/internationalhompage" : "/"}>{t("allJobsComponent.client")}</Link>
                            </Button> */}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-grow max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8 overflow-x-hidden w-full">
                {/* Header with job count and navigation */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{t("allJobsComponent.availableJobs")}</h1>
                        <p className="text-gray-600">
                            {isLoading || isLoadingQuotes || isInitialLoad 
                                ? t("allJobsComponent.loading")
                                : totalCount > 0 
                                    ? t("allJobsComponent.showingJobs", { start: startItem, end: endItem, total: totalCount })
                                    : t("allJobsComponent.noJobsAvailable")
                            }
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full sm:w-auto">
                        {/* Navigation Buttons */}
                        {totalCount > 0 && (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        const newPage = Math.max(1, currentPage - 1);
                                        setCurrentPage(newPage);
                                        localStorage.setItem("allJobsCurrentPage", newPage.toString());
                                    }}
                                    disabled={currentPage === 1 || isLoading || isLoadingQuotes || isInitialLoad}
                                    className="flex items-center gap-2 text-sm"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    <span className="hidden sm:inline">{t("allJobsComponent.previous")}</span>
                                </Button>

                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        const newPage = currentPage + 1;
                                        setCurrentPage(newPage);
                                        localStorage.setItem("allJobsCurrentPage", newPage.toString());
                                    }}
                                    disabled={!hasNextPage || isLoading || isLoadingQuotes || isInitialLoad}
                                    className="flex items-center gap-2 text-sm"
                                >
                                    <span className="hidden sm:inline">{t("allJobsComponent.next")}</span>
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </>
                        )}

                        <Button
                            onClick={handleRefresh}
                            variant="outline"
                            disabled={isLoading || isLoadingQuotes || isInitialLoad}
                            className="flex items-center gap-2 text-sm"
                        >
                            <RefreshCw className={`h-4 w-4 ${isLoading || isLoadingQuotes || isInitialLoad ? "animate-spin" : ""}`} />
                            <span className="hidden sm:inline">{t("allJobsComponent.refresh")}</span>
                        </Button>
                    </div>
                </div>

                {/* Search Section */}
                <div className="mb-6 sm:mb-8 space-y-3 sm:space-y-4 md:space-y-0 md:flex md:gap-4 px-1 sm:px-0">
                    <div className="flex-1">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={t("allJobsComponent.searchProfessional")}
                                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                value={professionalSearch}
                                onChange={(e) => setProfessionalSearch(e.target.value)}
                            />
                            <Search className="absolute left-3 top-3.5 text-gray-400 h-5 w-5" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:flex gap-2 sm:gap-4">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={t("allJobsComponent.province")}
                                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                value={provinceSearch}
                                onChange={(e) => setProvinceSearch(e.target.value)}
                            />
                            <MapPin className="absolute left-3 top-3.5 text-gray-400 h-5 w-5" />
                        </div>

                        <div className="relative">
                            <input
                                type="text"
                                placeholder={t("allJobsComponent.regionDistrict")}
                                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                value={municipalitySearch}
                                onChange={(e) => setMunicipalitySearch(e.target.value)}
                            />
                            <Building className="absolute left-3 top-3.5 text-gray-400 h-5 w-5" />
                        </div>
                    </div>

                    {/* Sort Dropdown */}
                    <div className="relative">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                        >
                            <option value="newest">{t("allJobsComponent.newestFirst")}</option>
                            <option value="oldest">{t("allJobsComponent.oldestFirst")}</option>
                            <option value="budget_high">{t("allJobsComponent.budgetHighToLow")}</option>
                            <option value="budget_low">{t("allJobsComponent.budgetLowToHigh")}</option>
                            <option value="deadline_urgent">{t("allJobsComponent.deadlineUrgentFirst")}</option>
                            <option value="deadline_relaxed">{t("allJobsComponent.deadlineRelaxedFirst")}</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                {/* Quotes Grid */}
                {isLoading || isLoadingQuotes || isInitialLoad ? (
                    <div className="flex justify-center items-center py-12">
                        <Spinner color="blue" />
                        <span className="ml-3 text-gray-600">
                            {isLoadingQuotes ? t("allJobsComponent.loadingJobDetails") : t("allJobsComponent.loadingJobs")}
                        </span>
                    </div>
                ) : quotes.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="text-gray-400 mb-4">
                            <FileText className="h-16 w-16 mx-auto" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {totalCount === 0 ? t("allJobsComponent.noJobsFound") : t("allJobsComponent.noJobsMatchSearch")}
                        </h3>
                        <p className="text-gray-600">
                            {totalCount === 0
                                ? t("allJobsComponent.noJobsDesc")
                                : t("allJobsComponent.tryAdjustingCriteria")
                            }
                        </p>
                        {totalCount === 0 && (
                            <Button
                                onClick={handleRefresh}
                                className="mt-4"
                                variant="outline"
                            >
                                {t("allJobsComponent.refresh")}
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 px-1 sm:px-0">
                        {quotes.map(quote => renderQuoteCard(quote))}
                    </div>
                )}

                {/* Pagination */}
                {totalCount > 0 && (
                    <div className="mt-8 flex flex-col items-center gap-4 px-1 sm:px-0">
                        {/* Page Info */}
                        <div className="text-sm text-gray-600">
                            {t("allJobsComponent.showingJobs", { start: startItem, end: endItem, total: totalCount })}
                            {calculatedTotalPages > 1 && ` (Page ${currentPage} of ${calculatedTotalPages})`}
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex justify-center gap-4">
                            {/* Previous Button */}
                            <Button
                                variant="outline"
                                onClick={() => {
                                    const newPage = Math.max(1, currentPage - 1);
                                    setCurrentPage(newPage);
                                    localStorage.setItem("allJobsCurrentPage", newPage.toString());
                                }}
                                disabled={currentPage === 1 || isLoading || isLoadingQuotes || isInitialLoad}
                                className="flex items-center gap-2"
                            >
                                <ChevronLeft className="h-4 w-4" />
                                {t("allJobsComponent.previous")}
                            </Button>

                            {/* Page Numbers (only show if multiple pages) */}
                            {calculatedTotalPages > 1 && (
                                <div className="flex gap-2">
                                    {Array.from({ length: Math.min(5, calculatedTotalPages) }, (_, i) => {
                                        let pageNum;
                                        if (calculatedTotalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= calculatedTotalPages - 2) {
                                            pageNum = calculatedTotalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => {
                                                    setCurrentPage(pageNum);
                                                    localStorage.setItem("allJobsCurrentPage", pageNum.toString());
                                                }}
                                                disabled={isLoading || isLoadingQuotes || isInitialLoad}
                                                className={`px-3 py-2 rounded-lg text-sm ${currentPage === pageNum
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-white text-gray-600 hover:bg-gray-50 border"
                                                    } ${isLoading || isLoadingQuotes || isInitialLoad ? "opacity-50 cursor-not-allowed" : ""}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Next Button */}
                            <Button
                                variant="outline"
                                onClick={() => {
                                    const newPage = currentPage + 1;
                                    setCurrentPage(newPage);
                                    localStorage.setItem("allJobsCurrentPage", newPage.toString());
                                }}
                                disabled={!hasNextPage || isLoading || isLoadingQuotes || isInitialLoad}
                                className="flex items-center gap-2"
                            >
                                {t("allJobsComponent.next")}
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}

            </main>

            {/* Footer */}
            {/* <footer className="mt-auto bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col items-center gap-4">
            <nav className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <a href="/how-it-works" className="text-sm text-gray-600 hover:text-gray-900">How it Works</a>
              <a href="/emergency" className="text-sm text-gray-600 hover:text-gray-900">Emergency Services</a>
              <a href="/privacy" className="text-sm text-gray-600 hover:text-gray-900">Privacy</a>
              <a href="/terms" className="text-sm text-gray-600 hover:text-gray-900">Terms</a>
            </nav>
            <p className="text-sm text-gray-600">
              © {new Date().getFullYear()} Kokobeo - Goldman Services INC
            </p>
          </div>
        </div>
      </footer> */}

            {/* Modals */}
            <MediaModal
                isOpen={showMediaModal}
                onClose={() => setShowMediaModal(false)}
                media={selectedMedia}
            />
            <QuoteDetailsModal
                quote={showQuoteDetails}
                isOpen={!!showQuoteDetails}
                onClose={() => setShowQuoteDetails(null)}
                showChat={showChat}
                setShowChat={setShowChat}
                isAuthenticated={isAuthenticated}
                user={user}
                setSelectedQuoteForRQ={setSelectedQuoteForRQ}
                setShowRequestQuoteModal={setShowRequestQuoteModal}
            />
            <ChatModal
                isOpen={showChat}
                onClose={() => setShowChat(false)}
            />
            {showRequestQuoteModal && selectedQuoteForRQ && (
                <RequestQuoteModal
                    isOpen={true}
                    onClose={() => { setShowRequestQuoteModal(false); setSelectedQuoteForRQ(null); }}
                    professional={{ id: selectedQuoteForRQ?.userId?.id || selectedQuoteForRQ?.userId || "", name: maskPrivateInfo(selectedQuoteForRQ?.customer?.name, "name") }}
                    isProfessional={true}
                    selectedJobId={selectedQuoteForRQ?.id}
                    isSentDefault={false}
                    onOfferCreated={(offer: any) => {
                        setShowRequestQuoteModal(false);
                        setSelectedQuoteForRQ(null);
                        try {
                            const profId = offer?.user?.id || offer?.userId?.id || "";
                            const firstName = offer?.user?.firstName || offer?.userId?.firstName || "";
                            const lastName = offer?.user?.lastName || offer?.userId?.lastName || "";
                            const profName = encodeURIComponent(`${firstName} ${lastName}`.trim());
                            if (profId && offer?.id) {
                                if (Number(user?.role?.id) === RoleEnum.client) {
                                    window.location.href = "/client/MessageAndVideoCall?professionalId=" + profId + "&professionalName=" + profName + "&offerId=" + offer.id + "&from=myoffers";
                                    // navigate(`/client/MessageAndVideoCall?professionalId=${profId}&professionalName=${profName}&offerId=${offer.id}&from=myoffers`);
                                }
                                else {
                                    window.location.href = "/MessageAndVideoCall?professionalId=" + user.id + "&professionalName=" + encodeURIComponent(user.firstName + " " + user.lastName) + "&offerId=" + offer.id + "&from=myoffers&userId=" + offer.job.userId.id;
                                    // navigate(`/MessageAndVideoCall?professionalId=${user.id}&professionalName=${user.firstName} ${user.lastName}&offerId=${offer.id}&from=myoffers&userId=${offer.job.userId.id}`);
                                }
                            }
                        } catch (e) {
                            // swallow navigation errors
                        }
                    }}
                />
            )}
            <SettingsModal
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
            />
            <ProfileModal
                isOpen={showProfile}
                onClose={() => setShowProfile(false)}
            />

            {/* Review Modal */}
            <ReviewModal
                isOpen={showReviewModal}
                onClose={() => setShowReviewModal(false)}
                professional={selectedUserForReview}
            />

            {/* Quote Purchase Modal */}
            <QuotePurchaseModal
                isOpen={showQuotePurchaseModal}
                onClose={() => setShowQuotePurchaseModal(false)}
                onSuccess={(quote) => {
                    // Close the modal first
                    setShowQuotePurchaseModal(false);
                    // Show loader for 2 seconds
                    setShowFullScreenLoader(true);
                    localStorage.setItem("myOffersActiveTab", "purchased");
                    // Navigate to myoffers after 2 seconds
                    setTimeout(() => {
                        setShowFullScreenLoader(false);
                        navigate("/myoffers");
                    }, 2000);
                }}
                quote={selectedQuoteForPurchase}
                userCredits={userCredits}
                refetchCredits={refetchCredits}
                useEmergencyCredits={selectedQuoteForPurchase?.useEmergencyCredits}
                emergencyAvailableCredits={userBalance?.emergencyAvailableCredits}
                emergencyUsedCredits={userBalance?.emergencyUsedCredits}
            />

            {/* Profile Completion Dialog */}
            <ProfileCompletionDialog
                isOpen={showProfileCompletionDialog}
                onClose={() => setShowProfileCompletionDialog(false)}
                onGoToProfile={navigateToProfile}
                userRole={user?.role?.id}
            />

            {/* Verification Requirement Dialog */}
            <VerificationRequirementDialog
                isOpen={showVerificationDialog}
                onClose={() => setShowVerificationDialog(false)}
                title={t("verification.accountVerificationRequired")}
                message={t("verification.completeVerificationToAccess")}
                showProgress={true}
                showMissingSteps={true}
            />

            {/* Billing Address Requirement Dialog */}
            <BillingAddressRequirementDialog
                isOpen={showBillingAddressDialog}
                onClose={() => {
                    setShowBillingAddressDialog(false);
                    setSelectedQuoteForBilling(null);
                }}
                onBillingSaved={handleBillingSaved}
                userRole={Number(user?.role?.id)}
                action="payment"
            />

            {/* Footer */}
            {!isLoading && !isLoadingQuotes && !isInitialLoad && (
                <footer className="mt-auto w-full">
                    <SharedFooter3 />
                </footer>
            )}
        </div>
        </FooterModalProvider>
    );
};

export default AllJobsComponent;

